import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Tab, Tabs, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import FeatureNotice from './FeatureNotice';
import { countries, getStatesForCountry } from '../utils/countriesStates';
import OrgPrintChrome from './OrgPrintChrome';
import LocationSelect from './LocationSelect';
import useSectionTab from '../hooks/useSectionTab';
import StockAdjustments from './StockAdjustments';
import StockAgeing from './StockAgeing';
import GrnBillChoice, { emptyGrnBill } from './GrnBillChoice';
import { GstRegistrationModal, useGstEnabled } from './GstRegistration';
import { hasAnyRole } from '../utils/permissionUtils';
import { todayISO } from '../utils/dates';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
import ModuleAlerts from './ModuleAlerts';
import { formatDate } from '../utils/dateFormat';
function formatGrnReceiveLocation(grn) {
  if (!grn) return '-';
  const type = (grn.receivingLocationType || '').toUpperCase();
  if (type === 'WAREHOUSE' && grn.warehouse?.name) {
    return `Warehouse: ${grn.warehouse.name}`;
  }
  if (type === 'RETAIL_STORE' && grn.retailStore?.name) {
    return `Retail Store: ${grn.retailStore.name}`;
  }
  if (type === 'PRODUCTION_UNIT' && grn.productionUnit?.name) {
    return `Production Unit: ${grn.productionUnit.name}`;
  }
  if (grn.warehouse?.name) return `Warehouse: ${grn.warehouse.name}`;
  if (grn.retailStore?.name) return `Retail Store: ${grn.retailStore.name}`;
  if (grn.productionUnit?.name) return `Production Unit: ${grn.productionUnit.name}`;
  return '-';
}

function formatGinIssueLocation(gin) {
  if (!gin) return '-';
  const type = (gin.issuingLocationType || '').toUpperCase();
  if (type === 'WAREHOUSE' && gin.warehouse?.name) {
    return `Warehouse: ${gin.warehouse.name}`;
  }
  if (type === 'RETAIL_STORE' && gin.retailStore?.name) {
    return `Retail Store: ${gin.retailStore.name}`;
  }
  if (type === 'PRODUCTION_UNIT' && gin.sourceProductionUnit?.name) {
    return `Production Unit: ${gin.sourceProductionUnit.name}`;
  }
  if (gin.warehouse?.name) return `Warehouse: ${gin.warehouse.name}`;
  if (gin.retailStore?.name) return `Retail Store: ${gin.retailStore.name}`;
  if (gin.sourceProductionUnit?.name) return `Production Unit: ${gin.sourceProductionUnit.name}`;
  return '-';
}


// Section keys, in tab order; the first is where a first visit opens
const INVENTORY_SECTIONS = ['stock-levels', 'stock-transfer-orders', 'grns', 'gins'];

function Inventory() {
  const { user, expiryEnabled } = useAuth();
  // E-way bills for stock transfers: the roles that move stock (Org Admin included)
  const canMoveStock = hasAnyRole(user, ['ORG_ADMIN', 'INV_OPR', 'INV_MGR', 'PROD_MGR']);
  const gstEnabled = useGstEnabled(canMoveStock);
  const [ewayTransfer, setEwayTransfer] = useState(null);
  const [loading, setLoading] = useState(true);

  // The open section is in the address (/inventory/<section>)
  const canAdjustStock = hasAnyRole(user, ['ORG_ADMIN', 'INV_MGR', 'PROD_MGR']);
  const [activeTab, setActiveTab] = useSectionTab('/inventory', [
    ...INVENTORY_SECTIONS,
    ...(canMoveStock ? ['stock-adjustments'] : []),
    ...(canAdjustStock ? ['stock-ageing'] : [])
  ]);
  const [organizationCurrency, setOrganizationCurrency] = useState('USD'); // Default to USD
  
  // Warehouse state (for dropdowns only, not for CRUD)
  const [warehouses, setWarehouses] = useState([]);
  
  // Stock levels state
  const [stockLevels, setStockLevels] = useState([]);
  const [stockFilterLocation, setStockFilterLocation] = useState('ALL'); // 'ALL' or 'WAREHOUSE:1', 'RETAIL_STORE:2', 'PRODUCTION_UNIT:3'
  const [showStockTransactions, setShowStockTransactions] = useState(false);
  const [stockTransactions, setStockTransactions] = useState([]);
  const [selectedStockItem, setSelectedStockItem] = useState(null);

  // GRN state
  const [grns, setGrns] = useState([]);
  const [showGRNModal, setShowGRNModal] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [organizationMaterials, setOrganizationMaterials] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [grnFormData, setGrnFormData] = useState({
    grnNumber: '',
    grnDate: todayISO(),
    warehouseId: '',
    receivingLocationType: 'WAREHOUSE',
    receivingLocationId: '',
    purchaseOrderId: '',
    procurementSurveyId: '',
    workOrderId: '',
    stockTransferOrderId: '',
    movementTypeId: '',
    remarks: '',
    status: 'DRAFT',
    ...emptyGrnBill,
    lineItems: []
  });
  const [grnLineItems, setGrnLineItems] = useState([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: '', orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
  const [grnServiceItems, setGrnServiceItems] = useState([]); // Service items from PO or manual (survey)
  const [organizationServices, setOrganizationServices] = useState([]);
  const [unusedWeighBridgeReceipts, setUnusedWeighBridgeReceipts] = useState([]);
  const [approvedProcurementSurveys, setApprovedProcurementSurveys] = useState([]);
  
  // Print view states
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printType, setPrintType] = useState(null); // 'GRN' | 'GIN' | 'STO'
  
  // Help pane state

  // GIN state
  const [gins, setGins] = useState([]);
  const [showGINModal, setShowGINModal] = useState(false);
  const [selectedGIN, setSelectedGIN] = useState(null);
  const [ginFormData, setGinFormData] = useState({
    ginNumber: '',
    ginDate: todayISO(),
    warehouseId: '',
    issuingLocationType: 'WAREHOUSE', // WAREHOUSE | RETAIL_STORE | PRODUCTION_UNIT
    issuingLocationId: '',
    movementTypeId: '',
    salesOrderId: '', // For SALES_ORDER reference type (placeholder for future)
    workOrderId: '', // For WORK_ORDER reference type
    purchaseOrderId: '', // For PURCHASE_ORDER reference type (returns to vendor)
    stockTransferOrderId: '', // For STOCK_TRANSFER_ORDER reference type
    referenceNumber: '', // Legacy field - kept for backward compatibility
    customerName: '',
    department: '',
    remarks: '',
    status: 'DRAFT',
    lineItems: []
  });
  const [ginLineItems, setGinLineItems] = useState([{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }]);
  const [inventoryLotsForGIN, setInventoryLotsForGIN] = useState([]);
  const [ginOriginalLotIssuedByLotId, setGinOriginalLotIssuedByLotId] = useState({});
  const [ginWarehouseAvailableByItemId, setGinWarehouseAvailableByItemId] = useState({});

  // Vendors state (needed for GRN dropdown)
  const [vendors, setVendors] = useState([]);
  
  // Purchase Orders state (needed for GRN dropdown)
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  
  // Work Orders state (needed for GRN dropdown when movement type is WORK_ORDER)
  const [workOrders, setWorkOrders] = useState([]);
  // Work orders with items issued but not all received (for GRN "Receive at Production Unit" reference dropdown)
  const [workOrdersIncompleteReceipts, setWorkOrdersIncompleteReceipts] = useState([]);

  // Goods movement types: Inbound for GRN, Outbound for GIN
  const [inboundMovementTypes, setInboundMovementTypes] = useState([]);
  const [outboundMovementTypes, setOutboundMovementTypes] = useState([]);

  // Production Units (for GIN when Movement Type = Issue to Production Unit)
  const [productionUnits, setProductionUnits] = useState([]);

  // Retail Stores (for GRN receiving location)
  const [retailStores, setRetailStores] = useState([]);

  // Stock Transfer Orders state
  const [stockTransferOrders, setStockTransferOrders] = useState([]);
  const [showStockTransferOrderModal, setShowStockTransferOrderModal] = useState(false);
  const [selectedStockTransferOrder, setSelectedStockTransferOrder] = useState(null);
  const [stockTransferOrderFormData, setStockTransferOrderFormData] = useState({
    transferNumber: '',
    transferDate: todayISO(),
    status: 'DRAFT',
    sourceLocationType: 'WAREHOUSE',
    sourceLocationId: '',
    destinationLocationType: 'WAREHOUSE',
    destinationLocationId: '',
    remarks: ''
  });
  const [stockTransferOrderLineItems, setStockTransferOrderLineItems] = useState([{ itemId: '', unitOfMeasurementId: '', quantity: '', remarks: '' }]);
  const [showSTOTransactionsModal, setShowSTOTransactionsModal] = useState(false);
  const [selectedSTOForTransactions, setSelectedSTOForTransactions] = useState(null);
  const [stoTransactions, setStoTransactions] = useState({ gins: [], grns: [] });
  const [loadingSTOTransactions, setLoadingSTOTransactions] = useState(false);

  // Initial load
  useEffect(() => {
    fetchStockLevels();
    fetchWarehouses(); // For filter dropdown
  }, []);

  useEffect(() => {
    if (activeTab === 'stock-levels') {
      fetchStockLevels();
      fetchWarehouses();
      fetchRetailStores();
      fetchProductionUnits();
    } else if (activeTab === 'stock-ageing') {
      fetchOrganizationCurrency();
    } else if (activeTab === 'stock-adjustments') {
      fetchWarehouses();
      fetchRetailStores();
      fetchProductionUnits();
      fetchOrganizationCurrency();
    } else if (activeTab === 'stock-transfer-orders') {
      fetchStockTransferOrders();
      fetchWarehouses();
      fetchRetailStores();
      fetchProductionUnits();
      if (items.length === 0) fetchItems();
      if (uoms.length === 0) fetchUOMs();
    } else if (activeTab === 'grns') {
      fetchGRNs();
      fetchWarehouses(); // For dropdown
      fetchRetailStores(); // For receiving location dropdown
      fetchProductionUnits(); // For receiving location dropdown (Production Units)
      fetchPurchaseOrders(); // For GRN PO dropdown
      fetchWorkOrders(); // For GRN Work Order dropdown
      fetchStockTransferOrders(); // For GRN Stock Transfer Order dropdown (Reference Order when referenceType is STOCK_TRANSFER_ORDER)
      fetchInboundMovementTypes(); // For GRN movement type dropdown
      fetchOrganizationCurrency(); // For currency formatting in print view
      if (items.length === 0) fetchItems();
      if (products.length === 0) fetchProducts();
      if (vendors.length === 0) fetchVendors();
      if (uoms.length === 0) fetchUOMs();
    } else if (activeTab === 'gins') {
      fetchGINs();
      fetchWarehouses(); // For issuing location dropdown
      fetchRetailStores(); // For issuing location dropdown
      fetchWorkOrders(); // For GIN Work Order dropdown (Reference Order when referenceType is WORK_ORDER)
      fetchStockTransferOrders(); // For GIN Stock Transfer Order dropdown (Reference Order when referenceType is STOCK_TRANSFER_ORDER)
      fetchOrganizationMaterials(); // For GIN line items: materials (raw materials)
      fetchProducts(); // For GIN line items: finished goods/products
      fetchOutboundMovementTypes(); // For GIN movement type dropdown
      fetchProductionUnits(); // For GIN issuing location dropdown, and Customer Name when Issue to Production Unit
      if (items.length === 0) fetchItems();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'grns' || activeTab === 'gins') {
      if (uoms.length === 0) fetchUOMs();
    }
    // Reset transactions view when switching away from stock-levels tab
    if (activeTab !== 'stock-levels' && showStockTransactions) {
      setShowStockTransactions(false);
      setStockTransactions([]);
      setSelectedStockItem(null);
    }
  }, [activeTab]);

  // When GRN receiving location is Production Unit, fetch work orders with incomplete receipts for Reference Order dropdown
  useEffect(() => {
    if (activeTab !== 'grns') return;
    const isProductionUnit = (grnFormData.receivingLocationType || '').toUpperCase() === 'PRODUCTION_UNIT';
    const productionUnitId = grnFormData.receivingLocationId ? String(grnFormData.receivingLocationId).trim() : '';
    if (isProductionUnit && productionUnitId) {
      fetchWorkOrdersIncompleteReceipts(productionUnitId);
    } else {
      setWorkOrdersIncompleteReceipts([]);
    }
  }, [activeTab, grnFormData.receivingLocationType, grnFormData.receivingLocationId]);

  // When GIN modal is open and an issuing location is selected, fetch inventory lots for "Inventory Lot" dropdown
  useEffect(() => {
    if (!showGINModal || !ginFormData.issuingLocationId) {
      setInventoryLotsForGIN([]);
      return;
    }
    const includeLotIds = (selectedGIN && ginLineItems.length > 0)
      ? ginLineItems.map(li => li.grnLineItemId).filter(Boolean).join(',')
      : '';
    fetchInventoryLotsForGIN(ginFormData.issuingLocationType, ginFormData.issuingLocationId, null, includeLotIds);
  }, [showGINModal, ginFormData.issuingLocationType, ginFormData.issuingLocationId, selectedGIN, ginLineItems.map(li => li.grnLineItemId).join(',')]);

  // Fetch issuing-location available qty per item on GIN lines (shown when no lot is selected)
  useEffect(() => {
    if (!showGINModal || !ginFormData.issuingLocationId) {
      setGinWarehouseAvailableByItemId({});
      return;
    }
    const itemIds = [...new Set(ginLineItems.map(li => li.itemId).filter(Boolean).map(String))];
    if (itemIds.length === 0) {
      setGinWarehouseAvailableByItemId({});
      return;
    }
    let cancelled = false;
    const fetchAvailable = async () => {
      const next = {};
      await Promise.all(itemIds.map(async (itemId) => {
        try {
          const params = {
            locationType: ginFormData.issuingLocationType || 'WAREHOUSE',
            locationId: ginFormData.issuingLocationId,
            itemId
          };
          if (selectedGIN?.id) {
            params.excludeGinId = selectedGIN.id;
          }
          const response = await axios.get('/api/inventory/stock-availability', { params });
          next[itemId] = parseFloat(response.data?.availableQuantity ?? 0) || 0;
        } catch (error) {
          console.error('Error fetching issuing location available stock:', error);
          next[itemId] = null;
        }
      }));
      if (!cancelled) {
        setGinWarehouseAvailableByItemId(next);
      }
    };
    fetchAvailable();
    return () => { cancelled = true; };
  }, [showGINModal, ginFormData.issuingLocationType, ginFormData.issuingLocationId, selectedGIN?.id, ginLineItems.map(li => li.itemId).join(',')]);

  // Sync linkedMaterialLineItemIndex for service items when grnLineItems changes
  useEffect(() => {
    if (grnServiceItems.length > 0 && grnLineItems.length > 0) {
      let hasChanges = false;
      const updated = grnServiceItems.map((si, idx) => {
        // If service has linkedMaterialItemId but no linkedMaterialLineItemIndex, find it
        if (si.linkedMaterialItemId && (!si.linkedMaterialLineItemIndex || si.linkedMaterialLineItemIndex === '')) {
          const serviceItemId = String(si.linkedMaterialItemId);
          const foundIndex = grnLineItems.findIndex(li => {
            if (!li.itemId) return false;
            return String(li.itemId) === serviceItemId;
          });
          
          if (foundIndex >= 0) {
            hasChanges = true;
            return {
              ...si,
              linkedMaterialLineItemIndex: foundIndex.toString()
            };
          }
        }
        return si;
      });
      
      if (hasChanges) {
        setGrnServiceItems(updated);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnLineItems.map(li => `${li.itemId || ''}-${li.receivedQuantity || ''}`).join(',')]);

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get('/api/inventory/warehouses');
      setWarehouses(response.data);
    } catch (error) {
      // Only show toast if not already showing another error
      if (activeTab !== 'stock-levels') {
        toast.error('Error fetching warehouses');
      }
      console.error(error);
    }
  };

  const fetchRetailStores = async () => {
    try {
      const response = await axios.get('/api/inventory/retail-stores/active');
      setRetailStores(response.data || []);
    } catch (error) {
      console.error('Error fetching retail stores for GRN receiving locations:', error);
    }
  };

  const fetchStockTransferOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/stock-transfer-orders');
      const sorted = [...(response.data || [])].sort((a, b) => new Date(b.transferDate) - new Date(a.transferDate));
      setStockTransferOrders(sorted);
    } catch (error) {
      toast.error('Error fetching stock transfer orders');
      console.error(error);
      setStockTransferOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStockTransferOrderLocationDisplay = (order, type) => {
    if (type === 'source') {
      if (order.sourceWarehouse) return `Warehouse: ${order.sourceWarehouse.name}`;
      if (order.sourceRetailStore) return `Retail Store: ${order.sourceRetailStore.name}`;
      if (order.sourceProductionUnit) return `Production Unit: ${order.sourceProductionUnit.name}`;
    } else {
      if (order.destinationWarehouse) return `Warehouse: ${order.destinationWarehouse.name}`;
      if (order.destinationRetailStore) return `Retail Store: ${order.destinationRetailStore.name}`;
      if (order.destinationProductionUnit) return `Production Unit: ${order.destinationProductionUnit.name}`;
    }
    return '-';
  };

  const handleCreateStockTransferOrder = () => {
    setStockTransferOrderFormData({
      transferNumber: '',
      transferDate: todayISO(),
      status: 'DRAFT',
      sourceLocationType: 'WAREHOUSE',
      sourceLocationId: '',
      destinationLocationType: 'WAREHOUSE',
      destinationLocationId: '',
      remarks: ''
    });
    setStockTransferOrderLineItems([{ itemId: '', unitOfMeasurementId: '', quantity: '', remarks: '' }]);
    setSelectedStockTransferOrder(null);
    setShowStockTransferOrderModal(true);
  };

  const handleEditStockTransferOrder = async (order) => {
    try {
      const res = await axios.get(`/api/inventory/stock-transfer-orders/${order.id}`);
      const o = res.data;
      const srcType = o.sourceLocationType || (o.sourceWarehouse ? 'WAREHOUSE' : (o.sourceRetailStore ? 'RETAIL_STORE' : 'PRODUCTION_UNIT'));
      const srcId = o.sourceWarehouse?.id || o.sourceRetailStore?.id || o.sourceProductionUnit?.id || '';
      const destType = o.destinationLocationType || (o.destinationWarehouse ? 'WAREHOUSE' : (o.destinationRetailStore ? 'RETAIL_STORE' : 'PRODUCTION_UNIT'));
      const destId = o.destinationWarehouse?.id || o.destinationRetailStore?.id || o.destinationProductionUnit?.id || '';
      setStockTransferOrderFormData({
        transferNumber: o.transferNumber || '',
        transferDate: o.transferDate || todayISO(),
        status: o.status || 'DRAFT',
        sourceLocationType: srcType,
        sourceLocationId: srcId || '',
        destinationLocationType: destType,
        destinationLocationId: destId || '',
        remarks: o.remarks || ''
      });
      setStockTransferOrderLineItems(
        (o.lineItems && o.lineItems.length > 0)
          ? o.lineItems.map(li => ({
              itemId: li.item?.id || '',
              unitOfMeasurementId: li.unitOfMeasurement?.id || '',
              quantity: li.quantity != null ? String(li.quantity) : '',
              remarks: li.remarks || ''
            }))
          : [{ itemId: '', unitOfMeasurementId: '', quantity: '', remarks: '' }]
      );
      setSelectedStockTransferOrder(o);
      setShowStockTransferOrderModal(true);
    } catch (err) {
      toast.error(err.response?.data || 'Error loading stock transfer order');
    }
  };

  const handleDeleteStockTransferOrder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this stock transfer order?')) return;
    try {
      await axios.delete(`/api/inventory/stock-transfer-orders/${id}`);
      toast.success('Stock transfer order deleted');
      fetchStockTransferOrders();
    } catch (error) {
      const msg = error.response?.data || 'Error deleting stock transfer order';
      toast.error(typeof msg === 'string' ? msg : 'Error deleting stock transfer order');
    }
  };

  const handleViewSTOTransactions = async (order) => {
    setSelectedSTOForTransactions(order);
    setShowSTOTransactionsModal(true);
    setLoadingSTOTransactions(true);
    setStoTransactions({ gins: [], grns: [] });
    try {
      const response = await axios.get(`/api/inventory/stock-transfer-orders/${order.id}/transactions`);
      setStoTransactions(response.data || { gins: [], grns: [] });
    } catch (error) {
      toast.error(error.response?.data || 'Failed to load STO transactions');
      setStoTransactions({ gins: [], grns: [] });
    } finally {
      setLoadingSTOTransactions(false);
    }
  };

  const handleSaveStockTransferOrder = async (e) => {
    e.preventDefault();
    const srcVal = `${stockTransferOrderFormData.sourceLocationType}:${stockTransferOrderFormData.sourceLocationId}`;
    const destVal = `${stockTransferOrderFormData.destinationLocationType}:${stockTransferOrderFormData.destinationLocationId}`;
    if (srcVal === destVal && stockTransferOrderFormData.sourceLocationId) {
      toast.error('Source and destination cannot be the same location');
      return;
    }
    const lineItems = stockTransferOrderLineItems
      .filter(li => li.itemId && li.quantity && parseFloat(li.quantity) > 0)
      .map(li => ({
        item: { id: parseInt(li.itemId) },
        unitOfMeasurement: li.unitOfMeasurementId ? { id: parseInt(li.unitOfMeasurementId) } : null,
        quantity: parseFloat(li.quantity),
        remarks: li.remarks || null
      }));
    if (lineItems.length === 0) {
      toast.error('Add at least one line item with item and positive quantity');
      return;
    }
    const payload = {
      transferNumber: stockTransferOrderFormData.transferNumber || null,
      transferDate: stockTransferOrderFormData.transferDate,
      status: stockTransferOrderFormData.status || 'DRAFT',
      sourceLocationType: stockTransferOrderFormData.sourceLocationType,
      sourceWarehouse: stockTransferOrderFormData.sourceLocationType === 'WAREHOUSE' && stockTransferOrderFormData.sourceLocationId ? { id: parseInt(stockTransferOrderFormData.sourceLocationId) } : null,
      sourceRetailStore: stockTransferOrderFormData.sourceLocationType === 'RETAIL_STORE' && stockTransferOrderFormData.sourceLocationId ? { id: parseInt(stockTransferOrderFormData.sourceLocationId) } : null,
      sourceProductionUnit: stockTransferOrderFormData.sourceLocationType === 'PRODUCTION_UNIT' && stockTransferOrderFormData.sourceLocationId ? { id: parseInt(stockTransferOrderFormData.sourceLocationId) } : null,
      destinationLocationType: stockTransferOrderFormData.destinationLocationType,
      destinationWarehouse: stockTransferOrderFormData.destinationLocationType === 'WAREHOUSE' && stockTransferOrderFormData.destinationLocationId ? { id: parseInt(stockTransferOrderFormData.destinationLocationId) } : null,
      destinationRetailStore: stockTransferOrderFormData.destinationLocationType === 'RETAIL_STORE' && stockTransferOrderFormData.destinationLocationId ? { id: parseInt(stockTransferOrderFormData.destinationLocationId) } : null,
      destinationProductionUnit: stockTransferOrderFormData.destinationLocationType === 'PRODUCTION_UNIT' && stockTransferOrderFormData.destinationLocationId ? { id: parseInt(stockTransferOrderFormData.destinationLocationId) } : null,
      remarks: stockTransferOrderFormData.remarks || null,
      lineItems
    };
    try {
      if (selectedStockTransferOrder) {
        await axios.put(`/api/inventory/stock-transfer-orders/${selectedStockTransferOrder.id}`, payload);
        toast.success('Stock transfer order updated');
      } else {
        await axios.post('/api/inventory/stock-transfer-orders', payload);
        toast.success('Stock transfer order created');
      }
      setShowStockTransferOrderModal(false);
      fetchStockTransferOrders();
    } catch (error) {
      const msg = error.response?.data || 'Error saving stock transfer order';
      toast.error(typeof msg === 'string' ? msg : 'Error saving stock transfer order');
    }
  };

  const addStockTransferOrderLineItem = () => {
    setStockTransferOrderLineItems(prev => [...prev, { itemId: '', unitOfMeasurementId: '', quantity: '', remarks: '' }]);
  };

  const removeStockTransferOrderLineItem = (index) => {
    setStockTransferOrderLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateStockTransferOrderLineItem = (index, field, value) => {
    setStockTransferOrderLineItems(prev => prev.map((li, i) => i === index ? { ...li, [field]: value } : li));
  };

  const fetchStockLevels = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/stock-levels');
      setStockLevels(response.data || []);
    } catch (error) {
      toast.error('Error fetching stock levels: ' + (error.response?.data?.message || error.message || 'Unknown error'));
      console.error('Stock levels error:', error);
      setStockLevels([]); // Set empty array on error to prevent infinite loading
    } finally {
      setLoading(false);
    }
  };

  const fetchStockTransactions = async (locationType, locationId, itemId, itemName, locationName) => {
    try {
      setLoading(true);
      await fetchOrganizationCurrency();
      const params = { itemId };
      if (locationType === 'WAREHOUSE') {
        params.warehouseId = locationId;
      } else {
        params.locationType = locationType;
        params.locationId = locationId;
      }
      const response = await axios.get('/api/inventory/stock-transactions', { params });
      setStockTransactions(response.data || []);
      setSelectedStockItem({ itemName, locationName, locationType, locationId, itemId });
      setShowStockTransactions(true);
    } catch (error) {
      toast.error('Error fetching stock transactions: ' + (error.response?.data?.message || error.message || 'Unknown error'));
      console.error('Stock transactions error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStockLevels = () => {
    setShowStockTransactions(false);
    setStockTransactions([]);
    setSelectedStockItem(null);
  };

  const fetchGRNs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/grns');
      // Sort by grnDate in reverse chronological order (latest first)
      const sortedData = [...response.data].sort((a, b) => {
        const dateA = new Date(a.grnDate);
        const dateB = new Date(b.grnDate);
        return dateB - dateA; // Descending order (latest first)
      });
      setGrns(sortedData);
    } catch (error) {
      toast.error('Error fetching GRNs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGINs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/gins');
      // Sort by ginDate in reverse chronological order (latest first)
      const sortedData = [...response.data].sort((a, b) => {
        const dateA = new Date(a.ginDate);
        const dateB = new Date(b.ginDate);
        return dateB - dateA; // Descending order (latest first)
      });
      setGins(sortedData);
    } catch (error) {
      toast.error('Error fetching GINs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInboundMovementTypes = async () => {
    try {
      const response = await axios.get('/api/inventory/goods-movement-types/direction/INBOUND');
      setInboundMovementTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching inbound movement types:', error);
    }
  };

  const fetchOutboundMovementTypes = async () => {
    try {
      const response = await axios.get('/api/inventory/goods-movement-types/direction/OUTBOUND');
      setOutboundMovementTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching outbound movement types:', error);
    }
  };

  const fetchProductionUnits = async () => {
    try {
      const response = await axios.get('/api/inventory/production-units/active');
      setProductionUnits(response.data || []);
    } catch (error) {
      console.error('Error fetching production units:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get('/api/inventory/items');
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products');
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchOrganizationMaterials = async () => {
    try {
      const response = await axios.get('/api/organization-materials');
      setOrganizationMaterials(response.data || []);
    } catch (error) {
      console.error('Error fetching organization materials:', error);
    }
  };

  const fetchUOMs = async () => {
    try {
      const response = await axios.get('/api/inventory/units-of-measurement');
      setUoms(response.data);
    } catch (error) {
      console.error('Error fetching UOMs:', error);
      toast.error('Error fetching units of measurement');
    }
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/vendors');
      setVendors(response.data);
    } catch (error) {
      // Read-only stock roles can't see vendors; the dropdown just stays empty for them
      if (error.response?.status !== 403) toast.error('Error fetching vendors');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationCurrency = async () => {
    try {
      if (user?.organizationId) {
        const response = await axios.get(`/api/organizations/${user.organizationId}`);
        const currency = response.data?.defaultCurrency || 'USD';
        setOrganizationCurrency(currency);
      }
    } catch (error) {
      console.error('Error fetching organization currency:', error);
      // Keep default USD on error
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return `${organizationCurrency} 0.00`;
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `${organizationCurrency} ${numAmount.toFixed(2)}`;
  };

  const fetchWorkOrders = async () => {
    try {
      const response = await axios.get('/api/production/work-orders');
      setWorkOrders(response.data || []);
    } catch (error) {
      console.error('Error fetching work orders:', error);
    }
  };

  const fetchInventoryLotsForGIN = async (locationType, locationId, itemId = null, includeLotIds = '') => {
    if (!locationId) {
      setInventoryLotsForGIN([]);
      return;
    }
    try {
      const params = { locationType: locationType || 'WAREHOUSE', locationId };
      if (itemId) params.itemId = itemId;
      if (includeLotIds) params.includeLotIds = includeLotIds;
      const response = await axios.get('/api/inventory/inventory-lots', { params });
      setInventoryLotsForGIN(response.data || []);
    } catch (error) {
      console.error('Error fetching inventory lots:', error);
      setInventoryLotsForGIN([]);
    }
  };

  const fetchWorkOrdersIncompleteReceipts = async (productionUnitId) => {
    if (!productionUnitId) {
      setWorkOrdersIncompleteReceipts([]);
      return;
    }
    try {
      const response = await axios.get('/api/production/work-orders/incomplete-receipts', {
        params: { productionUnitId }
      });
      setWorkOrdersIncompleteReceipts(response.data || []);
    } catch (error) {
      console.error('Error fetching work orders with incomplete receipts:', error);
      setWorkOrdersIncompleteReceipts([]);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/purchase-orders');
      // Sort by poDate in reverse chronological order (latest first)
      const sortedData = [...response.data].sort((a, b) => {
        const dateA = new Date(a.poDate);
        const dateB = new Date(b.poDate);
        return dateB - dateA; // Descending order (latest first)
      });
      setPurchaseOrders(sortedData);
    } catch (error) {
      // Read-only stock roles can't see purchase orders; the dropdown just stays empty for them
      if (error.response?.status !== 403) toast.error('Error fetching purchase orders');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationServices = async () => {
    try {
      const response = await axios.get('/api/organization-services');
      setOrganizationServices(response.data || []);
    } catch (error) {
      console.error('Error fetching organization services:', error);
      setOrganizationServices([]);
    }
  };

  const fetchApprovedProcurementSurveys = async () => {
    try {
      const response = await axios.get('/api/inventory/procurement-surveys', { params: { status: 'USABLE' } });
      setApprovedProcurementSurveys(response.data || []);
    } catch (error) {
      console.error('Error fetching open procurement surveys:', error);
      setApprovedProcurementSurveys([]);
    }
  };

  const handleViewPrint = async (data, type) => {
    // Ensure organization currency is loaded before showing print view
    await fetchOrganizationCurrency();

    let payload = data;
    if (type === 'STO') {
      try {
        const [orderRes, txRes] = await Promise.all([
          axios.get(`/api/inventory/stock-transfer-orders/${data.id}`),
          axios.get(`/api/inventory/stock-transfer-orders/${data.id}/transactions`)
        ]);
        const tx = txRes.data || { gins: [], grns: [] };
        payload = {
          ...orderRes.data,
          stoTransactions: { gins: tx.gins || [], grns: tx.grns || [] }
        };
      } catch (error) {
        const msg = error.response?.data || 'Error loading stock transfer order for print';
        toast.error(typeof msg === 'string' ? msg : 'Error loading stock transfer order for print');
        return;
      }
    }

    // For GRN, separate material and service line items
    if (type === 'GRN' && payload.lineItems) {
      payload.materialLineItems = payload.lineItems.filter(li => !li.lineType || li.lineType === 'MATERIAL');
      payload.serviceItems = payload.lineItems.filter(li => li.lineType === 'SERVICE').map(li => ({
        service: li.service,
        vendor: li.vendor,
        quantity: li.receivedQuantity,
        unitPrice: li.unitPrice,
        lineTotal: li.lineTotal,
        unitOfMeasurement: li.unitOfMeasurement,
        vendorLotNumber: li.vendorLotNumber,
        internalLotNumber: li.internalLotNumber
      }));
      // Keep lineItems as material items for backward compatibility
      payload.lineItems = payload.materialLineItems;
    }

    setPrintData(payload);
    setPrintType(type);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchUnusedWeighBridgeReceipts = async (excludeGrnId = null) => {
    try {
      const params = {};
      if (excludeGrnId) {
        params.excludeGrnId = excludeGrnId;
      }
      const response = await axios.get('/api/inventory/weigh-bridge-receipts/unused', { params });
      const receipts = response.data || [];
      setUnusedWeighBridgeReceipts(receipts);
      return receipts;
    } catch (error) {
      console.error('Error fetching unused weigh bridge receipts:', error);
      setUnusedWeighBridgeReceipts([]);
      return [];
    }
  };

  const handleCreateGRN = () => {
    setGrnFormData({
      grnNumber: '',
      grnDate: todayISO(),
      warehouseId: '',
      receivingLocationType: 'WAREHOUSE',
      receivingLocationId: '',
      purchaseOrderId: '',
      procurementSurveyId: '',
      workOrderId: '',
      stockTransferOrderId: '',
      movementTypeId: '',
      remarks: '',
      status: 'DRAFT',
      ...emptyGrnBill,
      lineItems: []
    });
    const newInternalLotNumber = generateInternalLotNumber();
    setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
    setGrnServiceItems([]);
    setSelectedGRN(null);
    // Ensure purchase orders are loaded for the dropdown
    if (purchaseOrders.length === 0) {
      fetchPurchaseOrders();
    }
    fetchApprovedProcurementSurveys();
    fetchOrganizationServices();
    // Fetch unused weigh bridge receipts
    fetchUnusedWeighBridgeReceipts();
    setShowGRNModal(true);
  };

  const handleEditGRN = async (grn) => {
    const inferredLocationType =
      grn.receivingLocationType ||
      (grn.retailStore ? 'RETAIL_STORE' : (grn.productionUnit ? 'PRODUCTION_UNIT' : 'WAREHOUSE'));
    const receivingLocationType = inferredLocationType.toUpperCase();
    const receivingLocationId =
      receivingLocationType === 'RETAIL_STORE'
        ? (grn.retailStore?.id || '')
        : receivingLocationType === 'PRODUCTION_UNIT'
          ? (grn.productionUnit?.id || '')
          : (grn.warehouse?.id || '');

    setGrnFormData({
      grnNumber: grn.grnNumber,
      grnDate: grn.grnDate,
      warehouseId: receivingLocationType === 'WAREHOUSE' ? (grn.warehouse?.id || '') : '',
      receivingLocationType,
      receivingLocationId: receivingLocationId || '',
      purchaseOrderId: grn.purchaseOrder?.id || '',
      procurementSurveyId: grn.procurementSurvey?.id || '',
      workOrderId: grn.workOrder?.id || '',
      stockTransferOrderId: grn.stockTransferOrder?.id || '',
      movementTypeId: grn.goodsMovementType?.id || '',
      remarks: grn.remarks || '',
      status: grn.status,
      ...emptyGrnBill,
      againstInvoiceId: grn.againstInvoiceId || ''
    });
    
    // Fetch unused weigh bridge receipts (needed to find selected WBR; exclude this GRN so its WBRs stay selectable)
    const unusedWbrs = await fetchUnusedWeighBridgeReceipts(grn.id);
    await fetchApprovedProcurementSurveys();
    
    // Load received/pending quantities if PO exists (for display purposes only)
    let receivedQuantitiesMap = {};
    if (grn.purchaseOrder?.id) {
      try {
        const response = await axios.get(`/api/inventory/purchase-orders/${grn.purchaseOrder.id}/line-items-with-received`);
        const { lineItems } = response.data;
        if (lineItems) {
          lineItems.forEach(poLineItem => {
            if (poLineItem.item?.id) {
              receivedQuantitiesMap[poLineItem.item.id] = {
                orderedQuantity: poLineItem.orderedQuantity || poLineItem.quantity || '',
                receivedQuantityTotal: poLineItem.receivedQuantity || '0',
                pendingQuantity: poLineItem.pendingQuantity || '',
                uomSymbol: poLineItem.unitOfMeasurement?.symbol || poLineItem.unitOfMeasurement?.name || ''
              };
            }
          });
        }
      } catch (error) {
        console.error('Error loading PO line items for edit:', error);
      }
    }
    
    // Load expected quantities if Work Order exists (for display purposes only)
    let woQuantitiesMap = {};
    if (grn.workOrder?.id) {
      try {
        const woResponse = await axios.get(`/api/production/work-orders/${grn.workOrder.id}`);
        const workOrder = woResponse.data;
        const outputProducts = (await axios.get(`/api/production/work-orders/${grn.workOrder.id}/bom-output-products`)).data || [];
        const quantityMultiplier = resolveBomScaleFactor(outputProducts, workOrder.quantityToProduce);
        
        outputProducts.forEach(outputProduct => {
          if (outputProduct.item?.id) {
            const baseQty = parseFloat(outputProduct.baseQuantity || 0);
            const expectedQuantity = (baseQty * quantityMultiplier).toFixed(3);
            woQuantitiesMap[outputProduct.item.id] = {
              orderedQuantity: expectedQuantity,
              receivedQuantityTotal: '0', // Will be calculated from existing GRNs
              pendingQuantity: expectedQuantity,
              uomSymbol: outputProduct.unitOfMeasurement?.symbol || outputProduct.unitOfMeasurement?.name || '',
              outputType: outputProduct.outputType
            };
          }
        });
      } catch (error) {
        console.error('Error loading WO BOM output products for edit:', error);
      }
    }
    
    // Separate material and service line items from GRN
    const materialLineItems = grn.lineItems?.filter(li => !li.lineType || li.lineType === 'MATERIAL') || [];
    const serviceLineItems = grn.lineItems?.filter(li => li.lineType === 'SERVICE') || [];
    
    // Map service items from GRN line items
    const serviceItemsData = serviceLineItems.map((li, serviceIdx) => {
      // Find the index of the linked material line item
      let linkedMaterialLineItemIndex = '';
      if (li.linkedMaterialLineItem?.id) {
        // Find the material line item index by matching the linked item's item ID
        const linkedItemId = li.linkedMaterialLineItem.item?.id;
        if (linkedItemId) {
          const materialIndex = materialLineItems.findIndex(mli => mli.item?.id === linkedItemId);
          if (materialIndex >= 0) {
            linkedMaterialLineItemIndex = materialIndex.toString();
          }
        }
      }
      
      return {
        id: li.id,
        serviceId: li.service?.id || '',
        serviceName: li.service?.name || '',
        quantity: li.receivedQuantity || 0,
        unitPrice: li.unitPrice || 0,
        lineTotal: li.lineTotal || 0,
        linkedMaterialItemId: li.linkedMaterialLineItem?.item?.id || null,
        linkedMaterialLineItemId: li.linkedMaterialLineItem?.id || null,
        linkedMaterialLineItemIndex: linkedMaterialLineItemIndex,
        vendor: li.vendor || null,
        vendorId: li.vendor?.id ? String(li.vendor.id) : '',
        description: '',
        vendorLotNumber: li.vendorLotNumber || '',
        internalLotNumber: li.internalLotNumber || '',
        fromPO: !!grn.purchaseOrder
      };
    });
    
    // Set service items (only for PURCHASE_ORDER flow)
    if (grn.workOrder?.id) {
      setGrnServiceItems([]); // No service items for Work Order flow
    } else {
      setGrnServiceItems(serviceItemsData);
    }
    
    // Map material line items only
    const mappedLineItems = materialLineItems.map(li => {
      const quantity = parseFloat(li.receivedQuantity) || 0;
      const price = parseFloat(li.unitPrice) || 0;
      const total = (quantity * price).toFixed(2);
      const itemId = li.item?.id;
      const poData = receivedQuantitiesMap[itemId] || {};
      const woData = woQuantitiesMap[itemId] || {};
      const isFromWO = !!grn.workOrder?.id;
      
      // Find weigh bridge receipt by RST number if it exists
      let weighBridgeReceiptId = '';
      if (!isFromWO && li.weighBridgeReceiptNo) {
        const wbr = unusedWbrs.find(r => r.rstNumber === li.weighBridgeReceiptNo);
        if (wbr) {
          weighBridgeReceiptId = wbr.id.toString();
        }
      }
      
      return {
        itemId: itemId || '',
        vendorId: li.vendor?.id || '',
        unitOfMeasurementId: li.unitOfMeasurement?.id || '',
        receivedQuantity: li.receivedQuantity || '',
        unitPrice: li.unitPrice || '',
        lineTotal: total,
        weighBridgeReceiptNo: li.weighBridgeReceiptNo || '',
        weighBridgeReceiptId: weighBridgeReceiptId,
        vendorLotNumber: li.vendorLotNumber || '',
        internalLotNumber: li.internalLotNumber || '',
        expiryDate: li.expiryDate || '',
        orderedQuantity: isFromWO ? woData.orderedQuantity : poData.orderedQuantity || '',
        receivedQuantityTotal: isFromWO ? woData.receivedQuantityTotal : poData.receivedQuantityTotal || '',
        pendingQuantity: isFromWO ? woData.pendingQuantity : poData.pendingQuantity || '',
        uomSymbol: isFromWO ? woData.uomSymbol : poData.uomSymbol || '',
        isFromPO: !!grn.purchaseOrder?.id,
        isFromWO: isFromWO,
        outputType: woData.outputType,
        additionalCosts: li.additionalCosts ? li.additionalCosts.toString() : '',
        allocatedServiceCost: li.allocatedServiceCost ? li.allocatedServiceCost.toString() : '0',
        landedCost: li.landedCost ? li.landedCost.toString() : '0',
        landedCostPerUnit: li.landedCostPerUnit ? li.landedCostPerUnit.toString() : '0'
      };
    }) || [{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: '', orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }];
    
    // Calculate service costs and apply to material items
    const withServiceCosts = calculateServiceCosts(mappedLineItems, serviceItemsData);
    setGrnLineItems(withServiceCosts);
    
    setSelectedGRN(grn);
    setShowGRNModal(true);
  };

  const handleDeleteGRN = async (id) => {
    if (!window.confirm('Are you sure you want to delete this GRN?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/grns/${id}`);
      toast.success('GRN deleted successfully');
      fetchGRNs();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting GRN';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting GRN');
    }
  };

  const handleReverseGRN = async (grn) => {
    const reason = window.prompt(
      `Reverse GRN ${grn.grnNumber}?\n\nThis posts contra ledger entries and removes the received stock. The GRN cannot be edited afterwards.\n\nReason for reversal:`
    );
    if (reason === null) {
      return; // cancelled
    }
    try {
      await axios.post(`/api/inventory/grns/${grn.id}/reverse`, { reason });
      toast.success('GRN reversed successfully');
      fetchGRNs();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error reversing GRN';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error reversing GRN');
    }
  };

  // Generate internal lot number in YYYYMMDD-SEQ format
  const generateInternalLotNumber = (existingItems = []) => {
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const datePrefix = `${yyyy}${mm}${dd}`;
    
    // Get sequence number for today - find highest existing sequence for today
    const todayPrefix = datePrefix;
    const itemsToCheck = existingItems.length > 0 ? existingItems : grnLineItems;
    const existingLotNumbers = itemsToCheck
      .map(item => item.internalLotNumber)
      .filter(lot => lot && lot.startsWith(todayPrefix + '-'))
      .map(lot => {
        const seqPart = lot.split('-')[1];
        return seqPart ? parseInt(seqPart) : 0;
      });
    
    const maxSeq = existingLotNumbers.length > 0 ? Math.max(...existingLotNumbers) : 0;
    const nextSeq = maxSeq + 1;
    
    return `${datePrefix}-${String(nextSeq).padStart(3, '0')}`;
  };

  const handleAddGRNLineItem = () => {
    const newInternalLotNumber = generateInternalLotNumber();
    setGrnLineItems([...grnLineItems, { itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
  };

  const handleMovementTypeChange = async (movementTypeId) => {
    // Find the selected movement type to check its referenceType
    const selectedMT = inboundMovementTypes.find(mt => {
      const mtId = typeof mt.id === 'string' ? parseInt(mt.id) : mt.id;
      const formId = typeof movementTypeId === 'string' ? parseInt(movementTypeId) : movementTypeId;
      return mtId === formId;
    });

    // If the new movement type restricts the receiving location to a specific type and the
    // currently selected location doesn't match, clear it so an invalid combination can't be submitted.
    const restrictToType = selectedMT?.locationType || null;
    const currentType = grnFormData.receivingLocationType || (grnFormData.warehouseId ? 'WAREHOUSE' : null);
    const locationClear = (restrictToType && currentType !== restrictToType)
      ? { warehouseId: '', receivingLocationType: 'WAREHOUSE', receivingLocationId: '' }
      : {};

    // Clear PO, WO, STO and Procurement Survey when movement type changes
    setGrnFormData(prev => ({
      ...prev,
      movementTypeId: movementTypeId,
      purchaseOrderId: '',
      workOrderId: '',
      stockTransferOrderId: '',
      procurementSurveyId: '',
      ...locationClear
    }));
    
    // Reset line items
    const newInternalLotNumber = generateInternalLotNumber();
    setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
    setGrnServiceItems([]);
  };

  const handleProcurementSurveyChange = (surveyId) => {
    setGrnFormData({
      ...grnFormData,
      procurementSurveyId: surveyId,
      purchaseOrderId: '',
      workOrderId: '',
      stockTransferOrderId: ''
    });

    if (!surveyId) {
      const newInternalLotNumber = generateInternalLotNumber();
      setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
      setGrnServiceItems([]);
      return;
    }

    const survey = approvedProcurementSurveys.find(s => String(s.id) === String(surveyId));
    if (!survey) {
      return;
    }

    let formattedUnitPrice = '';
    if (survey.price != null && survey.price !== '') {
      const price = parseFloat(survey.price);
      if (!isNaN(price)) {
        formattedUnitPrice = price.toFixed(2);
      }
    }

    const newInternalLotNumber = generateInternalLotNumber();
    setGrnLineItems([{
      itemId: survey.item?.id || '',
      vendorId: survey.vendor?.id || '',
      unitOfMeasurementId: survey.unitOfMeasurement?.id || '',
      receivedQuantity: '',
      unitPrice: formattedUnitPrice,
      lineTotal: '',
      weighBridgeReceiptNo: '',
      weighBridgeReceiptId: '',
      vendorLotNumber: '',
      internalLotNumber: newInternalLotNumber,
      orderedQuantity: survey.estimatedQuantity != null ? String(survey.estimatedQuantity) : '',
      receivedQuantityTotal: '0',
      pendingQuantity: survey.estimatedQuantity != null ? String(survey.estimatedQuantity) : '',
      uomSymbol: survey.unitOfMeasurement?.symbol || survey.unitOfMeasurement?.name || '',
      isFromPO: false,
      isFromSurvey: true,
      additionalCosts: '',
      allocatedServiceCost: 0,
      landedCost: 0,
      landedCostPerUnit: 0
    }]);

    // Prefill an H&T service row when the survey has an HT vendor (user picks the service catalog item)
    if (survey.htVendor?.id) {
      setGrnServiceItems([{
        serviceId: '',
        serviceName: '',
        vendorId: String(survey.htVendor.id),
        vendor: survey.htVendor,
        quantity: survey.estimatedQuantity != null ? String(survey.estimatedQuantity) : '',
        unitPrice: '',
        lineTotal: 0,
        linkedMaterialLineItemIndex: '0',
        linkedMaterialItemId: survey.item?.id || null,
        fromPO: false
      }]);
    } else {
      setGrnServiceItems([]);
    }
  };

  const handleWorkOrderChange = async (workOrderId) => {
    setGrnFormData({ ...grnFormData, workOrderId: workOrderId, purchaseOrderId: '', stockTransferOrderId: '' });
    
    if (!workOrderId) {
      // Clear line items if no WO selected
      const newInternalLotNumber = generateInternalLotNumber();
      setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
      setGrnServiceItems([]);
      return;
    }
    
    const isReceiveAtProductionUnit = (grnFormData.receivingLocationType || '').toUpperCase() === 'PRODUCTION_UNIT';
    
    try {
      if (isReceiveAtProductionUnit) {
        // Receive at Production Unit: GRN line items = raw materials issued by GINs, not yet received at Production Unit
        const txRes = await axios.get(`/api/production/work-orders/${workOrderId}/transactions`);
        const data = txRes.data || {};
        const totalIssuedByItem = {};
        (data.gins || []).forEach((gin) => {
          if (!gin || !gin.status || gin.status.toUpperCase() !== 'ISSUED') return;
          (gin.lineItems || []).forEach((li) => {
            if (!li || !li.itemId) return;
            const qty = li.issuedQuantity != null ? Number(li.issuedQuantity) : (li.quantity != null ? Number(li.quantity) : 0);
            if (!Number.isFinite(qty)) return;
            if (!totalIssuedByItem[li.itemId]) {
              totalIssuedByItem[li.itemId] = { issued: 0, issuedValue: 0, received: 0, itemName: li.itemName, unitOfMeasurementId: li.unitOfMeasurementId, uomSymbol: li.uomSymbol || '' };
            }
            totalIssuedByItem[li.itemId].issued += qty;
            const unitPrice = li.unitPrice != null
              ? Number(li.unitPrice)
              : (li.landedCostPerUnit != null ? Number(li.landedCostPerUnit) : NaN);
            if (Number.isFinite(unitPrice) && unitPrice > 0) {
              totalIssuedByItem[li.itemId].issuedValue += (qty * unitPrice);
              totalIssuedByItem[li.itemId].issuedWithCost =
                (totalIssuedByItem[li.itemId].issuedWithCost || 0) + qty;
            }
          });
        });
        const totalReceivedByItem = {};
        const selectedProductionUnitId = grnFormData.receivingLocationId ? String(grnFormData.receivingLocationId) : null;
        (data.grns || []).forEach((grn) => {
          if (!grn) return;
          // Only count GRNs received at the selected Production Unit (not warehouse/retail store)
          if (selectedProductionUnitId && grn.productionUnitId != null && String(grn.productionUnitId) !== selectedProductionUnitId) return;
          if (selectedProductionUnitId && grn.productionUnitId == null) return; // GRN at warehouse/store doesn't count
          (grn.lineItems || []).forEach((li) => {
            if (!li || !li.itemId || (li.lineType || 'MATERIAL') !== 'MATERIAL') return;
            const qty = li.receivedQuantity != null ? Number(li.receivedQuantity) : 0;
            if (!Number.isFinite(qty)) return;
            totalReceivedByItem[li.itemId] = (totalReceivedByItem[li.itemId] || 0) + qty;
          });
        });
        const newLineItems = [];
        Object.keys(totalIssuedByItem).forEach((itemId) => {
          const info = totalIssuedByItem[itemId];
          const received = totalReceivedByItem[itemId] || 0;
          const pending = Math.max(0, info.issued - received);
          if (pending <= 0) return;
          const avgIssuedUnitPrice = (info.issuedWithCost || 0) > 0
            ? (info.issuedValue / info.issuedWithCost)
            : 0;
          const pendingLandedCost = pending * avgIssuedUnitPrice;
          const newInternalLotNumber = generateInternalLotNumber(newLineItems);
          newLineItems.push({
            itemId,
            vendorId: '',
            unitOfMeasurementId: info.unitOfMeasurementId || '',
            receivedQuantity: '',
            unitPrice: Number.isFinite(avgIssuedUnitPrice) && avgIssuedUnitPrice > 0 ? avgIssuedUnitPrice.toFixed(4) : '',
            lineTotal: '',
            weighBridgeReceiptNo: '',
            weighBridgeReceiptId: '',
            vendorLotNumber: '',
            internalLotNumber: newInternalLotNumber,
            orderedQuantity: String(pending),
            receivedQuantityTotal: String(received),
            pendingQuantity: String(pending),
            uomSymbol: info.uomSymbol || '',
            isFromPO: false,
            isFromWO: true,
            additionalCosts: '',
            allocatedServiceCost: 0,
            landedCost: Number.isFinite(pendingLandedCost) && pendingLandedCost > 0 ? pendingLandedCost.toFixed(2) : 0,
            landedCostPerUnit: Number.isFinite(avgIssuedUnitPrice) && avgIssuedUnitPrice > 0 ? avgIssuedUnitPrice.toFixed(4) : 0
          });
        });
        if (newLineItems.length === 0) {
          const newInternalLotNumber = generateInternalLotNumber();
          setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, isFromWO: true, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
          toast.info('No raw materials pending receipt for this Work Order at Production Unit.');
        } else {
          setGrnLineItems(newLineItems);
        }
        setGrnServiceItems([]);
      } else {
        // Receive at Warehouse: GRN line items = BOM output products (finished goods)
        const response = await axios.get(`/api/production/work-orders/${workOrderId}/bom-output-products`);
        const outputProducts = response.data || [];
        
        if (outputProducts.length > 0) {
          const woResponse = await axios.get(`/api/production/work-orders/${workOrderId}`);
          const workOrder = woResponse.data;
          const quantityMultiplier = resolveBomScaleFactor(outputProducts, workOrder.quantityToProduce);
          
          const newLineItems = [];
          outputProducts.forEach((outputProduct) => {
            const baseQty = parseFloat(outputProduct.baseQuantity || 0);
            const expectedQuantity = (baseQty * quantityMultiplier).toFixed(3);
            const newInternalLotNumber = generateInternalLotNumber(newLineItems);
            newLineItems.push({
              itemId: outputProduct.item?.id || '',
              vendorId: '',
              unitOfMeasurementId: outputProduct.unitOfMeasurement?.id || '',
              receivedQuantity: '',
              unitPrice: '',
              lineTotal: '',
              weighBridgeReceiptNo: '',
              weighBridgeReceiptId: '',
              vendorLotNumber: '',
              internalLotNumber: newInternalLotNumber,
              orderedQuantity: expectedQuantity,
              receivedQuantityTotal: '0',
              pendingQuantity: expectedQuantity,
              uomSymbol: outputProduct.unitOfMeasurement?.symbol || outputProduct.unitOfMeasurement?.name || '',
              isFromPO: false,
              isFromWO: true,
              outputType: outputProduct.outputType,
              additionalCosts: '',
              allocatedServiceCost: 0,
              landedCost: 0,
              landedCostPerUnit: 0
            });
          });
          setGrnLineItems(newLineItems);
        } else {
          const newInternalLotNumber = generateInternalLotNumber();
          setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, isFromWO: true, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
        }
        setGrnServiceItems([]);
      }
    } catch (error) {
      console.error('Error loading Work Order data:', error);
      toast.error('Error loading Work Order data');
      const newInternalLotNumber = generateInternalLotNumber();
      setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, isFromWO: true, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
      setGrnServiceItems([]);
    }
  };

  const handlePurchaseOrderChange = async (poId) => {
    setGrnFormData({ ...grnFormData, purchaseOrderId: poId, procurementSurveyId: '', workOrderId: '', stockTransferOrderId: '' });
    
    if (!poId) {
      // Clear line items if no PO selected
      const newInternalLotNumber = generateInternalLotNumber();
      setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
      setGrnServiceItems([]);
      return;
    }
    
    try {
      // Fetch PO line items with received/pending quantities
      const response = await axios.get(`/api/inventory/purchase-orders/${poId}/line-items-with-received`);
      const { lineItems, serviceItems } = response.data;
      
      if (lineItems && lineItems.length > 0) {
        // Auto-populate GRN line items from PO line items
        const newLineItems = [];
        lineItems.forEach(poLineItem => {
          // Format unit price to 2 decimal places if it exists
          let formattedUnitPrice = '';
          if (poLineItem.unitPrice != null && poLineItem.unitPrice !== '') {
            const price = parseFloat(poLineItem.unitPrice);
            if (!isNaN(price)) {
              formattedUnitPrice = price.toFixed(2);
            }
          }
          
          // Filter out H&T and Transporter vendors (GRN line items cannot have these vendor types)
          let vendorId = '';
          if (poLineItem.vendor?.id) {
            // Check vendor type directly from PO response
            if (poLineItem.vendor.vendorType) {
              const vendorTypeName = poLineItem.vendor.vendorType.name.toUpperCase();
              // Only include vendor if it's NOT H&T or Transporter
              if (!vendorTypeName.includes('H&T') && !vendorTypeName.includes('TRANSPORTER')) {
                vendorId = poLineItem.vendor.id;
              }
            } else {
              // Include vendor if it has no vendor type
              vendorId = poLineItem.vendor.id;
            }
          }
          
          // Generate lot number based on items created so far
          const newInternalLotNumber = generateInternalLotNumber(newLineItems);
          newLineItems.push({
            itemId: poLineItem.item?.id || '',
            vendorId: vendorId,
            unitOfMeasurementId: poLineItem.unitOfMeasurement?.id || '',
            receivedQuantity: '', // User will enter the quantity being received now
            unitPrice: formattedUnitPrice,
            lineTotal: '',
            serviceCostAdjustment: 0, // Will be calculated from service items
            weighBridgeReceiptNo: '',
            weighBridgeReceiptId: '',
            vendorLotNumber: '',
            internalLotNumber: newInternalLotNumber,
            orderedQuantity: poLineItem.orderedQuantity || poLineItem.quantity || '',
            receivedQuantityTotal: poLineItem.receivedQuantity || '0',
            pendingQuantity: poLineItem.pendingQuantity || '',
            uomSymbol: poLineItem.unitOfMeasurement?.symbol || poLineItem.unitOfMeasurement?.name || '',
            isFromPO: true // Mark as from PO
          });
        });
        
        setGrnLineItems(newLineItems);
        
        // Store service items
        const newServiceItems = (serviceItems || []).map(serviceItem => {
          const unitPrice = parseFloat(serviceItem.unitPrice || 0);
          
          // If service is linked to a material item from PO, find the corresponding material line item
          let quantity = parseFloat(serviceItem.quantity || 0);
          let linkedMaterialLineItemIndex = '';
          
          if (serviceItem.linkedMaterialItemId) {
            // Find the material line item index by matching itemId (handle both string and number)
            const serviceItemId = String(serviceItem.linkedMaterialItemId);
            const materialIndex = newLineItems.findIndex(li => {
              if (!li.itemId) return false;
              return String(li.itemId) === serviceItemId;
            });
            if (materialIndex >= 0) {
              linkedMaterialLineItemIndex = materialIndex.toString();
              // For linked services, quantity will be set from material item's received quantity
              // We'll keep the PO quantity as initial value, but it will update when material quantity is entered
            }
          }
          
          const lineTotal = parseFloat(serviceItem.lineTotal || 0) || (quantity * unitPrice);
          
          return {
            id: serviceItem.id,
            serviceId: serviceItem.service?.id ? String(serviceItem.service.id) : '',
            serviceName: serviceItem.service?.name || '',
            quantity: quantity,
            unitPrice: unitPrice,
            lineTotal: lineTotal,
            linkedMaterialItemId: serviceItem.linkedMaterialItemId || null,
            linkedMaterialLineItemId: serviceItem.linkedMaterialLineItemId || null,
            linkedMaterialLineItemIndex: linkedMaterialLineItemIndex,
            vendor: serviceItem.vendor || null,
            vendorId: serviceItem.vendor?.id ? String(serviceItem.vendor.id) : '',
            description: serviceItem.description || '',
            remarks: serviceItem.remarks || '',
            fromPO: true
          };
        });
        
        setGrnServiceItems(newServiceItems);
        
        // Calculate service costs and apply to material items
        const withServiceCosts = calculateServiceCosts(newLineItems, newServiceItems);
        setGrnLineItems(withServiceCosts);
        
        setGrnFormData({ ...grnFormData, purchaseOrderId: poId });
      } else {
        toast.info('No material items with pending quantity found in this Purchase Order');
        const newInternalLotNumber = generateInternalLotNumber();
        setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
        setGrnServiceItems([]);
      }
    } catch (error) {
      console.error('Error loading PO line items:', error);
      toast.error('Failed to load Purchase Order line items');
    }
  };

  const handleGRNStockTransferOrderChange = async (stockTransferOrderId) => {
    setGrnFormData(prev => ({ ...prev, stockTransferOrderId: stockTransferOrderId || '', purchaseOrderId: '', workOrderId: '' }));
    if (!stockTransferOrderId) {
      const newInternalLotNumber = generateInternalLotNumber();
      setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
      setGrnServiceItems([]);
      return;
    }
    try {
      const response = await axios.get(`/api/inventory/stock-transfer-orders/${stockTransferOrderId}`);
      const sto = response.data;
      if (Array.isArray(sto.lineItems) && sto.lineItems.length > 0) {
        const newLineItems = sto.lineItems
          .filter(li => li.item && li.item.id)
          .map((li, idx) => {
            const qty = parseFloat(li.quantity) || 0;
            const newInternalLotNumber = generateInternalLotNumber();
            return {
              itemId: li.item.id.toString(),
              vendorId: '',
              unitOfMeasurementId: li.unitOfMeasurement?.id ? li.unitOfMeasurement.id.toString() : '',
              receivedQuantity: '',
              unitPrice: '',
              lineTotal: '',
              weighBridgeReceiptNo: '',
              weighBridgeReceiptId: '',
              vendorLotNumber: '',
              internalLotNumber: newInternalLotNumber,
              orderedQuantity: qty ? qty.toFixed(3) : '',
              receivedQuantityTotal: '0',
              pendingQuantity: qty ? qty.toFixed(3) : '',
              uomSymbol: li.unitOfMeasurement?.symbol || li.unitOfMeasurement?.name || '',
              isFromPO: false,
              additionalCosts: '',
              allocatedServiceCost: 0,
              landedCost: 0,
              landedCostPerUnit: 0
            };
          });
        setGrnLineItems(newLineItems);
        setGrnServiceItems([]);
      } else {
        const newInternalLotNumber = generateInternalLotNumber();
        setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
        setGrnServiceItems([]);
      }
    } catch (error) {
      console.error('Error loading Stock Transfer Order:', error);
      toast.error('Failed to load Stock Transfer Order line items');
      const newInternalLotNumber = generateInternalLotNumber();
      setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
      setGrnServiceItems([]);
    }
  };

  /** Uppercase reference type from selected GRN inbound movement type (matches Reference Order logic). */
  const getGrnFormReferenceTypeUpper = () => {
    const selectedMovementType = inboundMovementTypes.find((mt) => {
      const mtId = typeof mt.id === 'string' ? parseInt(mt.id, 10) : mt.id;
      const formId = typeof grnFormData.movementTypeId === 'string' ? parseInt(grnFormData.movementTypeId, 10) : grnFormData.movementTypeId;
      return mtId === formId;
    });
    if (!selectedMovementType) return 'PURCHASE_ORDER';
    let referenceType = (selectedMovementType.referenceType || 'PURCHASE_ORDER').toString().toUpperCase();
    if (!selectedMovementType.referenceType || String(selectedMovementType.referenceType).trim() === '') {
      const name = (selectedMovementType.name || '').toUpperCase();
      if (name.includes('PRODUCTION') || name.includes('WORK ORDER') || name.includes('WORKORDER')) {
        referenceType = 'WORK_ORDER';
      } else if (name.includes('TRANSFER') || name.includes('STOCK TRANSFER')) {
        referenceType = 'STOCK_TRANSFER_ORDER';
      }
    }
    return referenceType;
  };

  const isGrnInternalStockTransfer = () => getGrnFormReferenceTypeUpper() === 'STOCK_TRANSFER_ORDER';

  // Calculate and apply service costs to material items
  const calculateServiceCosts = (lineItems, serviceItems) => {
    if (!serviceItems || serviceItems.length === 0) {
      return lineItems.map(li => {
        // Calculate base total from quantity * price
        const baseTotal = (parseFloat(li.receivedQuantity) || 0) * (parseFloat(li.unitPrice) || 0);
        return { ...li, serviceCostAdjustment: 0, lineTotal: baseTotal.toFixed(2) };
      });
    }
    
    const updated = lineItems.map(li => {
      // Calculate base total from quantity * price (not from existing lineTotal which may include service costs)
      const baseTotal = (parseFloat(li.receivedQuantity) || 0) * (parseFloat(li.unitPrice) || 0);
      return { ...li, serviceCostAdjustment: 0, lineTotal: baseTotal.toFixed(2) };
    });
    
    // Separate linked and unlinked services
    const linkedServices = serviceItems.filter(si => si.linkedMaterialItemId);
    const unlinkedServices = serviceItems.filter(si => !si.linkedMaterialItemId);
    
    // Apply linked service costs to specific material items
    linkedServices.forEach(service => {
      const materialItemIndex = updated.findIndex(li => li.itemId === service.linkedMaterialItemId.toString());
      if (materialItemIndex >= 0) {
        const materialItem = updated[materialItemIndex];
        const receivedQty = parseFloat(materialItem.receivedQuantity) || 0;
        const orderedQty = parseFloat(materialItem.orderedQuantity) || 1;
        
        if (orderedQty > 0 && receivedQty > 0) {
          // Calculate cost based on received quantity ratio
          const ratio = receivedQty / orderedQty;
          const serviceCost = (parseFloat(service.lineTotal) || 0) * ratio;
          updated[materialItemIndex].serviceCostAdjustment = 
            (parseFloat(updated[materialItemIndex].serviceCostAdjustment) || 0) + serviceCost;
        }
      }
    });
    
    // Distribute unlinked service costs across all material items based on quantity
    // First calculate total ordered quantity to determine proportion of PO received in this GRN
    if (unlinkedServices.length > 0) {
      const totalReceivedQty = updated.reduce((sum, li) => {
        return sum + (parseFloat(li.receivedQuantity) || 0);
      }, 0);
      
      const totalOrderedQty = updated.reduce((sum, li) => {
        return sum + (parseFloat(li.orderedQuantity) || 0);
      }, 0);
      
      if (totalReceivedQty > 0 && totalOrderedQty > 0) {
        // Calculate proportion: how much of the PO is received in this GRN
        const grnProportion = totalReceivedQty / totalOrderedQty;
        
        // Calculate total unlinked service cost from PO
        const totalUnlinkedServiceCostFromPO = unlinkedServices.reduce((sum, si) => {
          return sum + (parseFloat(si.lineTotal) || 0);
        }, 0);
        
        // Apply proportion to get service cost for this GRN
        const serviceCostForThisGRN = totalUnlinkedServiceCostFromPO * grnProportion;
        
        // Now distribute this GRN's service cost proportionally across material items in this GRN
        updated.forEach((li, index) => {
          const receivedQty = parseFloat(li.receivedQuantity) || 0;
          if (receivedQty > 0) {
            const itemProportion = receivedQty / totalReceivedQty;
            const allocatedCost = serviceCostForThisGRN * itemProportion;
            updated[index].serviceCostAdjustment = 
              (parseFloat(updated[index].serviceCostAdjustment) || 0) + allocatedCost;
          }
        });
      }
    }
    
    // Update line totals with service cost adjustments and calculate landed cost
    updated.forEach((li, index) => {
      const baseTotal = (parseFloat(li.receivedQuantity) || 0) * (parseFloat(li.unitPrice) || 0);
      const serviceCost = parseFloat(li.serviceCostAdjustment) || 0;
      updated[index].lineTotal = (baseTotal + serviceCost).toFixed(2);
      
      // Update allocated service cost (for landed cost calculation)
      updated[index].allocatedServiceCost = serviceCost.toFixed(2);
      
      // Calculate landed cost = base cost + service cost + additional costs
      const additional = parseFloat(li.additionalCosts) || 0;
      const landedCost = baseTotal + serviceCost + additional;
      updated[index].landedCost = landedCost.toFixed(2);
      
      // Calculate landed cost per unit
      const quantity = parseFloat(li.receivedQuantity) || 0;
      if (quantity > 0) {
        updated[index].landedCostPerUnit = (landedCost / quantity).toFixed(4);
      } else {
        updated[index].landedCostPerUnit = '0.0000';
      }
    });
    
    return updated;
  };

  const handleRemoveGRNLineItem = (index) => {
    setGrnLineItems(grnLineItems.filter((_, i) => i !== index));
  };

  // Goods billed before they came: the bill's goods lines as GRN lines, valued as billed
  const fillGrnLinesFromBill = (bill) => {
    const filled = [];
    (bill.goods || []).filter((g) => g.itemId).forEach((g) => {
      const qty = Number(g.quantity) || 0;
      const total = Number(g.amount) || 0;
      const uom = uoms.find((u) => u.id === g.unitOfMeasurementId);
      filled.push({
        itemId: String(g.itemId), vendorId: String(bill.vendorId), unitOfMeasurementId: g.unitOfMeasurementId ? String(g.unitOfMeasurementId) : '',
        receivedQuantity: qty ? String(qty) : '', unitPrice: qty ? (total / qty).toFixed(2) : '', lineTotal: total.toFixed(2),
        weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: generateInternalLotNumber(filled.length ? filled : [{}]),
        orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: g.unitSymbol || (uom ? (uom.symbol || uom.name) : ''),
        isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: total.toFixed(2), landedCostPerUnit: qty ? (total / qty).toFixed(4) : '0.0000'
      });
    });
    if (filled.length === 0) { toast.warn('This bill has no items picked from the list; enter the lines yourself.'); return; }
    setGrnLineItems(filled);
  };

  const handleGRNLineItemChange = (index, field, value) => {
    const updated = [...grnLineItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // Calculate line total when quantity or unit price changes
    if (field === 'receivedQuantity' || field === 'unitPrice') {
      const quantity = parseFloat(updated[index].receivedQuantity) || 0;
      const price = parseFloat(updated[index].unitPrice) || 0;
      updated[index].lineTotal = (quantity * price).toFixed(2);
    }
    
    // Calculate landed cost when base cost, service cost, or additional costs change
    if (field === 'lineTotal' || field === 'allocatedServiceCost' || field === 'additionalCosts' || field === 'receivedQuantity') {
      const baseCost = parseFloat(updated[index].lineTotal) || 0;
      const serviceCost = parseFloat(updated[index].allocatedServiceCost) || 0;
      const additional = parseFloat(updated[index].additionalCosts) || 0;
      const landedCost = baseCost + serviceCost + additional;
      updated[index].landedCost = landedCost.toFixed(2);
      
      // Calculate landed cost per unit
      const quantity = parseFloat(updated[index].receivedQuantity) || 0;
      if (quantity > 0) {
        updated[index].landedCostPerUnit = (landedCost / quantity).toFixed(4);
      } else {
        updated[index].landedCostPerUnit = '0.0000';
      }
    }
    
    // Update UOM symbol when UOM is changed
    if (field === 'unitOfMeasurementId') {
      const selectedUom = uoms.find(uom => uom.id === parseInt(value));
      updated[index].uomSymbol = selectedUom ? (selectedUom.symbol || selectedUom.name) : '';
    }
    
    // When weigh bridge receipt is selected, auto-populate quantity from netWeight
    if (field === 'weighBridgeReceiptId') {
      if (!value) {
        updated[index].weighBridgeReceiptNo = '';
      } else {
        const selectedReceipt = unusedWeighBridgeReceipts.find(r => r.id === parseInt(value));
        if (selectedReceipt) {
          updated[index].weighBridgeReceiptNo = selectedReceipt.rstNumber;
          if (selectedReceipt.netWeight) {
            const newQuantity = parseFloat(selectedReceipt.netWeight).toFixed(2);
            updated[index].receivedQuantity = newQuantity;
            // Recalculate line total
            const quantity = parseFloat(updated[index].receivedQuantity) || 0;
            const price = parseFloat(updated[index].unitPrice) || 0;
            updated[index].lineTotal = (quantity * price).toFixed(2);

            // Prefer material from WBR when line has none
            if (!updated[index].itemId && selectedReceipt.material?.id) {
              updated[index].itemId = String(selectedReceipt.material.id);
            }
            if (!updated[index].vendorId && selectedReceipt.vendor?.id) {
              updated[index].vendorId = String(selectedReceipt.vendor.id);
            }

            // Update linked service quantities when weigh bridge receipt sets material quantity
            const materialItemId = updated[index].itemId;
            if (materialItemId) {
              const updatedServiceItems = grnServiceItems.map(si => {
                // Find the linked material index - check both linkedMaterialLineItemIndex and linkedMaterialItemId
                let linkedMaterialIndex = null;
                if (si.linkedMaterialLineItemIndex !== '' && si.linkedMaterialLineItemIndex !== null && si.linkedMaterialLineItemIndex !== undefined) {
                  linkedMaterialIndex = parseInt(si.linkedMaterialLineItemIndex);
                } else if (si.linkedMaterialItemId) {
                  // Find the material line item by matching itemId
                  linkedMaterialIndex = updated.findIndex(li => li.itemId && li.itemId.toString() === si.linkedMaterialItemId.toString());
                }

                // Check if this service is linked to the changed material item (by index or by itemId)
                const isLinkedToThisMaterial = linkedMaterialIndex === index ||
                  (si.linkedMaterialItemId && materialItemId.toString() === si.linkedMaterialItemId.toString());

                if (isLinkedToThisMaterial) {
                  const updatedQuantity = parseFloat(newQuantity);
                  const unitPrice = parseFloat(si.unitPrice) || 0;
                  const newLineTotal = updatedQuantity * unitPrice;
                  return {
                    ...si,
                    quantity: updatedQuantity,
                    lineTotal: newLineTotal
                  };
                }
                return si;
              });
              setGrnServiceItems(updatedServiceItems);
            }
          }
        }
      }
    }
    
    // If material quantity changed, update linked service quantities
    if (field === 'receivedQuantity') {
      const materialItemId = updated[index].itemId;
      if (materialItemId) {
        const newQuantity = parseFloat(value) || 0;
        // Update all service items linked to this material item
        const updatedServiceItems = grnServiceItems.map(si => {
          // Find the linked material index - check both linkedMaterialLineItemIndex and linkedMaterialItemId
          let linkedMaterialIndex = null;
          if (si.linkedMaterialLineItemIndex !== '' && si.linkedMaterialLineItemIndex !== null && si.linkedMaterialLineItemIndex !== undefined) {
            linkedMaterialIndex = parseInt(si.linkedMaterialLineItemIndex);
          } else if (si.linkedMaterialItemId) {
            // Find the material line item by matching itemId
            linkedMaterialIndex = updated.findIndex(li => li.itemId && li.itemId.toString() === si.linkedMaterialItemId.toString());
          }
          
          // Check if this service is linked to the changed material item (by index or by itemId)
          const isLinkedToThisMaterial = linkedMaterialIndex === index || 
            (si.linkedMaterialItemId && materialItemId.toString() === si.linkedMaterialItemId.toString());
          
          if (isLinkedToThisMaterial) {
            const updatedQuantity = newQuantity;
            const unitPrice = parseFloat(si.unitPrice) || 0;
            const newLineTotal = updatedQuantity * unitPrice;
            return {
              ...si,
              quantity: updatedQuantity,
              lineTotal: newLineTotal
            };
          }
          return si;
        });
        setGrnServiceItems(updatedServiceItems);
      }
    }
    
    // Recalculate service costs whenever quantity changes
    const withServiceCosts = calculateServiceCosts(updated, grnServiceItems);
    setGrnLineItems(withServiceCosts);
  };

  const handleSaveGRN = async (e) => {
    e.preventDefault();
    try {
      // Material line items
      const materialLineItems = grnLineItems
        .filter(li => li.itemId && li.receivedQuantity)
        .map(li => {
          // Filter out H&T and Transporter vendors before sending to backend
          let vendorRef = null;
          if (li.vendorId) {
            // Find vendor in vendors list to check its type
            const vendor = vendors.find(v => v.id === parseInt(li.vendorId));
            if (vendor) {
              if (vendor.vendorType) {
                const vendorTypeName = vendor.vendorType.name.toUpperCase();
                // Only include vendor if it's NOT H&T or Transporter
                if (!vendorTypeName.includes('H&T') && !vendorTypeName.includes('TRANSPORTER')) {
                  vendorRef = { id: parseInt(li.vendorId) };
                }
              } else {
                // Include vendor if it has no vendor type
                vendorRef = { id: parseInt(li.vendorId) };
              }
            }
          }
          
          return {
            lineType: 'MATERIAL',
            item: { id: parseInt(li.itemId) },
            service: null,
            vendor: vendorRef,
            unitOfMeasurement: li.unitOfMeasurementId ? { id: parseInt(li.unitOfMeasurementId) } : null,
            receivedQuantity: parseFloat(li.receivedQuantity),
            unitPrice: li.unitPrice ? parseFloat(li.unitPrice) : null,
            lineTotal: li.lineTotal ? parseFloat(li.lineTotal) : null,
            weighBridgeReceiptNo: li.weighBridgeReceiptNo || null,
            vendorLotNumber: li.vendorLotNumber || null,
            internalLotNumber: li.internalLotNumber || null,
            expiryDate: expiryEnabled && li.expiryDate ? li.expiryDate : null,
            additionalCosts: li.additionalCosts ? parseFloat(li.additionalCosts) : null,
            allocatedServiceCost: li.allocatedServiceCost ? parseFloat(li.allocatedServiceCost) : null,
            landedCost: li.landedCost ? parseFloat(li.landedCost) : null,
            landedCostPerUnit: li.landedCostPerUnit ? parseFloat(li.landedCostPerUnit) : null
          };
        });

      // Service line items - include all service items that have a serviceId and quantity > 0
      const filteredServiceSource = grnServiceItems.filter(si => {
          const serviceId = si.serviceId;
          const hasServiceId = serviceId !== null && serviceId !== undefined && serviceId !== '';
          
          let linkedMaterialIndex = null;
          if (si.linkedMaterialLineItemIndex !== '' && si.linkedMaterialLineItemIndex !== null && si.linkedMaterialLineItemIndex !== undefined) {
            linkedMaterialIndex = parseInt(si.linkedMaterialLineItemIndex);
          } else if (si.linkedMaterialItemId) {
            linkedMaterialIndex = materialLineItems.findIndex(li => li.item?.id === parseInt(si.linkedMaterialItemId));
          }
          
          let quantity;
          if (linkedMaterialIndex !== null && linkedMaterialIndex >= 0 && linkedMaterialIndex < materialLineItems.length) {
            quantity = parseFloat(materialLineItems[linkedMaterialIndex].receivedQuantity || 0);
          } else {
            quantity = parseFloat(si.quantity || 0);
          }
          
          return hasServiceId && !isNaN(quantity) && quantity > 0;
        });

      const serviceLineItems = filteredServiceSource.map((si) => {
          let linkedMaterialIndex = null;
          if (si.linkedMaterialLineItemIndex !== '' && si.linkedMaterialLineItemIndex !== null && si.linkedMaterialLineItemIndex !== undefined) {
            const materialIndex = parseInt(si.linkedMaterialLineItemIndex);
            if (materialIndex >= 0 && materialIndex < materialLineItems.length) {
              linkedMaterialIndex = materialIndex;
            }
          } else if (si.linkedMaterialItemId) {
            const materialIndex = materialLineItems.findIndex(li => li.item?.id === parseInt(si.linkedMaterialItemId));
            if (materialIndex >= 0) {
              linkedMaterialIndex = materialIndex;
            }
          }
          
          let quantity;
          if (linkedMaterialIndex !== null && linkedMaterialIndex >= 0 && linkedMaterialIndex < materialLineItems.length) {
            quantity = parseFloat(materialLineItems[linkedMaterialIndex].receivedQuantity || 0);
          } else {
            quantity = parseFloat(si.quantity || 0);
          }
          
          const unitPrice = si.unitPrice ? parseFloat(si.unitPrice) : null;
          const lineTotal = quantity * (unitPrice || 0);
          const vendorId = si.vendorId || si.vendor?.id;
          
          return {
            lineType: 'SERVICE',
            item: null,
            service: { id: parseInt(si.serviceId) },
            vendor: vendorId ? { id: parseInt(vendorId) } : null,
            unitOfMeasurement: null,
            receivedQuantity: quantity,
            unitPrice: unitPrice,
            lineTotal: lineTotal > 0 ? lineTotal : null,
            weighBridgeReceiptNo: null,
            remarks: si.remarks || '',
            linkedMaterialLineItem: null,
            _linkedMaterialIndex: linkedMaterialIndex
          };
        });

      // Combine material and service line items
      const lineItems = [...materialLineItems, ...serviceLineItems];
      
      // Map serviceIndex in combined array -> materialIndex (for backend link resolution)
      const serviceLinkMap = new Map();
      serviceLineItems.forEach((si, serviceIdx) => {
        if (si._linkedMaterialIndex !== null && si._linkedMaterialIndex !== undefined && !isNaN(si._linkedMaterialIndex)) {
          const materialIdx = parseInt(si._linkedMaterialIndex, 10);
          if (materialIdx >= 0 && materialIdx < materialLineItems.length) {
            serviceLinkMap.set(materialLineItems.length + serviceIdx, materialIdx);
          }
        }
        delete si._linkedMaterialIndex;
      });

      const locationType = (grnFormData.receivingLocationType || 'WAREHOUSE').toUpperCase();
      const isWarehouseLocation = locationType === 'WAREHOUSE';
      const isRetailLocation = locationType === 'RETAIL_STORE';
      const isProductionLocation = locationType === 'PRODUCTION_UNIT';
      const payload = {
        grnNumber: grnFormData.grnNumber || null,
        grnDate: grnFormData.grnDate,
        receivingLocationType: locationType,
        warehouse: isWarehouseLocation && grnFormData.warehouseId
          ? { id: parseInt(grnFormData.warehouseId) }
          : null,
        retailStore: isRetailLocation && grnFormData.receivingLocationId
          ? { id: parseInt(grnFormData.receivingLocationId) }
          : null,
        productionUnit: isProductionLocation && grnFormData.receivingLocationId
          ? { id: parseInt(grnFormData.receivingLocationId) }
          : null,
        purchaseOrder: grnFormData.purchaseOrderId ? { id: parseInt(grnFormData.purchaseOrderId) } : null,
        procurementSurvey: grnFormData.procurementSurveyId ? { id: parseInt(grnFormData.procurementSurveyId) } : null,
        workOrder: grnFormData.workOrderId ? { id: parseInt(grnFormData.workOrderId) } : null,
        stockTransferOrder: grnFormData.stockTransferOrderId ? { id: parseInt(grnFormData.stockTransferOrderId) } : null,
        goodsMovementType: grnFormData.movementTypeId ? { id: parseInt(grnFormData.movementTypeId) } : null,
        remarks: grnFormData.remarks || null,
        status: grnFormData.status || 'DRAFT',
        againstInvoiceId: grnFormData.againstInvoiceId ? Number(grnFormData.againstInvoiceId) : null,
        billStillToArrive: Boolean(grnFormData.billStillToArrive),
        notForOpenBills: Boolean(grnFormData.notForOpenBills),
        lineItems
      };

      // Add link metadata to payload for backend to process
      if (serviceLinkMap.size > 0) {
        payload._serviceLinkMetadata = Array.from(serviceLinkMap.entries()).map(([serviceIdx, materialIdx]) => ({
          serviceIndex: serviceIdx,
          materialIndex: materialIdx
        }));
      }

      // Debug: Log payload to see what's being sent
      console.log('GRN Save - Service Items State:', grnServiceItems);
      console.log('GRN Save - Material items count:', materialLineItems.length);
      console.log('GRN Save - Service items count:', serviceLineItems.length);
      console.log('GRN Save - Total line items count:', lineItems.length);
      console.log('GRN Save Payload:', JSON.stringify(payload, null, 2));

      if (selectedGRN) {
        await axios.put(`/api/inventory/grns/${selectedGRN.id}`, payload);
        toast.success('GRN updated successfully');
      } else {
        await axios.post('/api/inventory/grns', payload);
        toast.success('GRN created successfully');
      }
      setShowGRNModal(false);
      fetchGRNs();
    } catch (error) {
      console.error('GRN Save Error:', error);
      console.error('GRN Save Error Response:', error.response?.data);
      const data = error.response?.data;
      const errorMsg = typeof data === 'string' && data ? data
        : data?.message || data?.error
        || (error.response ? 'Error saving GRN' : 'Could not reach the server, so the GRN was not saved. Please try again.');
      toast.error(errorMsg);
    }
  };

  const handleCreateGIN = () => {
    setGinFormData({
      ginNumber: '',
      ginDate: todayISO(),
      warehouseId: '',
      issuingLocationType: 'WAREHOUSE',
      issuingLocationId: '',
      movementTypeId: '',
      salesOrderId: '',
      workOrderId: '',
      purchaseOrderId: '',
      stockTransferOrderId: '',
      referenceNumber: '',
      customerName: '',
      department: '',
      remarks: '',
      status: 'DRAFT',
      lineItems: []
    });
    setGinLineItems([{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }]);
    setGinOriginalLotIssuedByLotId({});
    setSelectedGIN(null);
    setShowGINModal(true);
  };

  const handleEditGIN = (gin) => {
    const issuingLocationType = (gin.issuingLocationType || 'WAREHOUSE').toUpperCase();
    const issuingLocationId = gin.warehouse?.id || gin.retailStore?.id || gin.sourceProductionUnit?.id || '';
    setGinFormData({
      ginNumber: gin.ginNumber,
      ginDate: gin.ginDate,
      warehouseId: gin.warehouse?.id || '',
      issuingLocationType,
      issuingLocationId,
      movementTypeId: gin.goodsMovementType?.id || '',
      salesOrderId: '', // Placeholder for future SalesOrder
      workOrderId: gin.workOrder?.id || '',
      purchaseOrderId: gin.purchaseOrder?.id || '',
      stockTransferOrderId: gin.stockTransferOrder?.id || '',
      referenceNumber: gin.referenceNumber || '',
      customerName: gin.customerName || '',
      department: gin.department || '',
      remarks: gin.remarks || '',
      status: gin.status
    });
    setGinLineItems(gin.lineItems?.map(li => ({
      itemId: li.item?.id || '',
      quantity: li.quantity || '',
      unitOfMeasurementId: li.unitOfMeasurement?.id || '',
      issuedQuantity: li.issuedQuantity || li.quantity || '',
      remarks: li.remarks || '',
      grnLineItemId: li.grnLineItem?.id || '',
      unitPrice: li.unitPrice != null ? String(li.unitPrice) : ''
    })) || [{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }]);
    const originalLotIssued = {};
    (gin.lineItems || []).forEach((li) => {
      const lotId = li.grnLineItem?.id;
      if (!lotId) return;
      const qty = li.issuedQuantity != null ? parseFloat(li.issuedQuantity) : parseFloat(li.quantity || 0);
      if (Number.isFinite(qty) && qty > 0) {
        originalLotIssued[lotId] = (originalLotIssued[lotId] || 0) + qty;
      }
    });
    setGinOriginalLotIssuedByLotId(originalLotIssued);
    setSelectedGIN(gin);
    setShowGINModal(true);
  };

  const handleDeleteGIN = async (id) => {
    if (!window.confirm('Are you sure you want to delete this GIN?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/gins/${id}`);
      toast.success('GIN deleted successfully');
      fetchGINs();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting GIN';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting GIN');
    }
  };

  const handleAddGINLineItem = () => {
    setGinLineItems([...ginLineItems, { itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }]);
  };

  const handleRemoveGINLineItem = (index) => {
    setGinLineItems(ginLineItems.filter((_, i) => i !== index));
  };

  const handleGINLineItemChange = (index, field, value) => {
    const updated = [...ginLineItems];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'quantity' && !updated[index].issuedQuantity) {
      updated[index].issuedQuantity = value;
    }
    setGinLineItems(updated);
  };

  const parseGinQty = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  };

  /** Scale WO qty against BOM primary output base (batch size). Legacy BOMs without outputs use base 1. */
  const resolveBomScaleFactor = (outputProducts, quantityToProduce) => {
    const qty = parseFloat(quantityToProduce || 0) || 0;
    const outputs = outputProducts || [];
    const primary = outputs.find(op => op.outputType === 'PRIMARY') || outputs[0];
    const primaryBase = parseFloat(primary?.baseQuantity || 0);
    if (primaryBase > 0) {
      return qty / primaryBase;
    }
    return qty;
  };

  const getRequestedQtyForLine = (lineItem) => {
    const issued = lineItem.issuedQuantity !== '' && lineItem.issuedQuantity !== null && lineItem.issuedQuantity !== undefined
      ? parseGinQty(lineItem.issuedQuantity)
      : parseGinQty(lineItem.quantity);
    return issued > 0 ? issued : 0;
  };

  const getAvailableQtyForLotLine = (lineIndex) => {
    const lineItem = ginLineItems[lineIndex];
    const lotId = lineItem?.grnLineItemId ? parseInt(lineItem.grnLineItemId, 10) : null;
    if (!lotId) return null;
    const lot = inventoryLotsForGIN.find(l => l.id === lotId);
    if (!lot) return null;

    const baseAvailable = parseGinQty(lot.availableQuantity);
    const originalForThisGIN = parseGinQty(ginOriginalLotIssuedByLotId[lotId] || 0);
    const draftOtherLines = ginLineItems.reduce((sum, li, idx) => {
      if (idx === lineIndex) return sum;
      if (!li.grnLineItemId || parseInt(li.grnLineItemId, 10) !== lotId) return sum;
      return sum + getRequestedQtyForLine(li);
    }, 0);

    return Math.max(baseAvailable + originalForThisGIN - draftOtherLines, 0);
  };

  const getWarehouseAvailableQtyForLine = (lineIndex) => {
    const lineItem = ginLineItems[lineIndex];
    if (!lineItem?.itemId || !ginFormData.issuingLocationId) return null;
    const itemKey = String(lineItem.itemId);
    if (!(itemKey in ginWarehouseAvailableByItemId) || ginWarehouseAvailableByItemId[itemKey] == null) {
      return null;
    }
    const baseAvailable = parseGinQty(ginWarehouseAvailableByItemId[itemKey]);
    const draftOtherLines = ginLineItems.reduce((sum, li, idx) => {
      if (idx === lineIndex) return sum;
      if (String(li.itemId) !== itemKey) return sum;
      return sum + getRequestedQtyForLine(li);
    }, 0);
    return Math.max(baseAvailable - draftOtherLines, 0);
  };

  const getLotOvershootError = (lineIndex) => {
    const lineItem = ginLineItems[lineIndex];
    const requested = getRequestedQtyForLine(lineItem);
    if (requested <= 0) return null;

    if (ginFormData.workOrderId && lineItem.woRemainingQty != null) {
      const itemKey = String(lineItem.itemId);
      const draftOtherLines = ginLineItems.reduce((sum, li, idx) => {
        if (idx === lineIndex) return sum;
        if (String(li.itemId) !== itemKey) return sum;
        return sum + getRequestedQtyForLine(li);
      }, 0);
      const remainingForLine = Math.max((parseFloat(lineItem.woRemainingQty) || 0) - draftOtherLines, 0);
      if (requested - remainingForLine > 0.000001) {
        return `Issue Qty (${requested.toFixed(3)}) exceeds Work Order remaining (${remainingForLine.toFixed(3)}). Already issued: ${(parseFloat(lineItem.woAlreadyIssued) || 0).toFixed(3)}.`;
      }
    }

    if (lineItem?.grnLineItemId) {
      const available = getAvailableQtyForLotLine(lineIndex);
      if (available === null) return null;
      if (requested - available > 0.000001) {
        return `Issue Qty (${requested.toFixed(3)}) exceeds lot available qty (${available.toFixed(3)}).`;
      }
      return null;
    }

    const warehouseAvailable = getWarehouseAvailableQtyForLine(lineIndex);
    if (warehouseAvailable === null) return null;
    if (requested - warehouseAvailable > 0.000001) {
      return `Issue Qty (${requested.toFixed(3)}) exceeds warehouse available qty (${warehouseAvailable.toFixed(3)}).`;
    }
    return null;
  };

  const handleGINLineItemLotSelect = (index, grnLineItemId) => {
    if (!grnLineItemId) {
      setGinLineItems(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], grnLineItemId: '', unitPrice: '', itemId: '', unitOfMeasurementId: '' };
        return updated;
      });
      return;
    }
    const lot = inventoryLotsForGIN.find(l => l.id === parseInt(grnLineItemId, 10));
    if (!lot) return;
    setGinLineItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        grnLineItemId: String(lot.id),
        itemId: String(lot.itemId || ''),
        unitOfMeasurementId: lot.unitOfMeasurementId ? String(lot.unitOfMeasurementId) : '',
        unitPrice: lot.landedCostPerUnit != null ? String(lot.landedCostPerUnit) : ''
      };
      return updated;
    });
  };

  const handleGINMovementTypeChange = (movementTypeId) => {
    const formId = typeof movementTypeId === 'string' ? parseInt(movementTypeId) : movementTypeId;
    const selectedMt = outboundMovementTypes.find(mt => (typeof mt.id === 'string' ? parseInt(mt.id) : mt.id) === formId);
    const isIssueToProductionUnit = selectedMt && (selectedMt.name || '').toLowerCase().includes('production unit');

    // If the new movement type restricts the issuing location to a specific type and the
    // currently selected location doesn't match, clear it so an invalid combination can't be submitted.
    const restrictToType = selectedMt?.locationType || null;
    const currentType = ginFormData.issuingLocationType || (ginFormData.warehouseId ? 'WAREHOUSE' : null);
    const locationClear = (restrictToType && currentType !== restrictToType)
      ? { warehouseId: '', issuingLocationType: 'WAREHOUSE', issuingLocationId: '' }
      : {};

    setGinFormData(prev => ({
      ...prev,
      movementTypeId: movementTypeId,
      salesOrderId: '',
      workOrderId: '',
      purchaseOrderId: '',
      stockTransferOrderId: '',
      ...locationClear,
      ...(isIssueToProductionUnit ? { department: 'Production', customerName: '' } : {})
    }));
  };

  const handleGINWorkOrderChange = async (workOrderId) => {
    // If no work order selected, just clear the reference and reset line items
    if (!workOrderId) {
      setGinFormData(prev => ({
        ...prev,
        workOrderId: '',
        purchaseOrderId: '',
        stockTransferOrderId: ''
      }));
      setGinLineItems([{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }]);
      return;
    }

    try {
      const [woResponse, txResponse] = await Promise.all([
        axios.get(`/api/production/work-orders/${workOrderId}`),
        axios.get(`/api/production/work-orders/${workOrderId}/transactions`)
      ]);
      const workOrder = woResponse.data;
      const gins = txResponse.data?.gins || [];

      const alreadyIssuedByItemId = {};
      gins.forEach(gin => {
        const status = (gin.status || '').toUpperCase();
        if (status !== 'ISSUED' && status !== 'PARTIAL' && status !== 'COMPLETED') return;
        // When editing this GIN, ignore its own prior quantities
        if (selectedGIN?.id && String(gin.id) === String(selectedGIN.id)) return;
        (gin.lineItems || []).forEach(li => {
          if (!li.itemId) return;
          const qty = parseFloat(li.issuedQuantity != null ? li.issuedQuantity : li.quantity) || 0;
          const key = String(li.itemId);
          alreadyIssuedByItemId[key] = (alreadyIssuedByItemId[key] || 0) + qty;
        });
      });

      let newLineItems = [];

      if (Array.isArray(workOrder.lineItems) && workOrder.lineItems.length > 0) {
        newLineItems = workOrder.lineItems
          .filter(li => li.item && li.item.id)
          .map(li => {
            const requiredQty = parseFloat(li.requiredQuantity ?? li.quantity ?? 0) || 0;
            const alreadyIssued = alreadyIssuedByItemId[String(li.item.id)] || 0;
            const remainingQty = Math.max(requiredQty - alreadyIssued, 0);

            return {
              itemId: li.item.id.toString(),
              quantity: remainingQty > 0 ? remainingQty.toFixed(3) : '0',
              unitOfMeasurementId: li.unitOfMeasurement?.id ? li.unitOfMeasurement.id.toString() : '',
              issuedQuantity: remainingQty > 0 ? remainingQty.toFixed(3) : '',
              remarks: '',
              grnLineItemId: '',
              unitPrice: '',
              woRequiredQty: requiredQty,
              woAlreadyIssued: alreadyIssued,
              woRemainingQty: remainingQty
            };
          })
          .filter(li => li.itemId);
      }

      if (newLineItems.length === 0) {
        newLineItems = [{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }];
      } else if (newLineItems.every(li => (parseFloat(li.woRemainingQty) || 0) <= 0)) {
        toast.info('All Work Order material requirements have already been issued. No remaining quantity to issue.');
      }

      setGinLineItems(newLineItems);

      // Update form with selected work order (warehouse stays as chosen on the GIN)
      setGinFormData(prev => ({
        ...prev,
        workOrderId,
        purchaseOrderId: ''
      }));
    } catch (error) {
      console.error('Error preloading GIN line items from Work Order:', error);
      toast.error('Error loading items from Work Order BOM');
      setGinLineItems([{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }]);
    }
  };

  const handleGINStockTransferOrderChange = async (stockTransferOrderId) => {
    if (!stockTransferOrderId) {
      setGinFormData(prev => ({
        ...prev,
        stockTransferOrderId: '',
        workOrderId: '',
        purchaseOrderId: ''
      }));
      setGinLineItems([{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }]);
      return;
    }

    try {
      const response = await axios.get(`/api/inventory/stock-transfer-orders/${stockTransferOrderId}`);
      const sto = response.data;

      let newLineItems = [];

      if (Array.isArray(sto.lineItems) && sto.lineItems.length > 0) {
        newLineItems = sto.lineItems
          .filter(li => li.item && li.item.id)
          .map(li => ({
            itemId: li.item.id.toString(),
            quantity: li.quantity ? parseFloat(li.quantity).toFixed(3) : '',
            unitOfMeasurementId: li.unitOfMeasurement?.id ? li.unitOfMeasurement.id.toString() : '',
            issuedQuantity: '',
            remarks: li.remarks || '',
            grnLineItemId: '',
            unitPrice: ''
          }))
          .filter(li => li.itemId);
      }

      if (newLineItems.length === 0) {
        newLineItems = [{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }];
      }

      setGinLineItems(newLineItems);

      let sourceType = null;
      let sourceLocId = null;
      if (sto.sourceLocationType === 'WAREHOUSE' && sto.sourceWarehouse?.id) {
        sourceType = 'WAREHOUSE';
        sourceLocId = sto.sourceWarehouse.id.toString();
      } else if (sto.sourceLocationType === 'RETAIL_STORE' && sto.sourceRetailStore?.id) {
        sourceType = 'RETAIL_STORE';
        sourceLocId = sto.sourceRetailStore.id.toString();
      } else if (sto.sourceLocationType === 'PRODUCTION_UNIT' && sto.sourceProductionUnit?.id) {
        sourceType = 'PRODUCTION_UNIT';
        sourceLocId = sto.sourceProductionUnit.id.toString();
      }

      setGinFormData(prev => {
        const hasLocation = !!prev.issuingLocationId;
        const nextType = hasLocation ? prev.issuingLocationType : (sourceType || prev.issuingLocationType);
        const nextLocId = hasLocation ? prev.issuingLocationId : (sourceLocId || prev.issuingLocationId);
        return {
          ...prev,
          stockTransferOrderId,
          workOrderId: '',
          purchaseOrderId: '',
          issuingLocationType: nextType,
          issuingLocationId: nextLocId,
          warehouseId: nextType === 'WAREHOUSE' ? nextLocId : ''
        };
      });
    } catch (error) {
      console.error('Error preloading GIN line items from Stock Transfer Order:', error);
      toast.error('Error loading items from Stock Transfer Order');
      setGinLineItems([{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }]);
    }
  };

  const handleSaveGIN = async (e) => {
    e.preventDefault();
    try {
      for (let i = 0; i < ginLineItems.length; i++) {
        const overshootError = getLotOvershootError(i);
        if (overshootError) {
          toast.error(`Line ${i + 1}: ${overshootError}`);
          return;
        }
      }

      // Inventory lot is mandatory on every issued line
      for (let i = 0; i < ginLineItems.length; i++) {
        const li = ginLineItems[i];
        if (!li.itemId) continue;
        const issueQty = li.issuedQuantity !== '' && li.issuedQuantity != null
          ? parseFloat(li.issuedQuantity)
          : parseFloat(li.quantity);
        if (Number.isFinite(issueQty) && issueQty > 0 && !li.grnLineItemId) {
          toast.error(`Line ${i + 1}: please select the inventory lot to issue from`);
          return;
        }
      }

      const lineItems = ginLineItems
        .filter(li => {
          if (!li.itemId) return false;
          const issueQty = li.issuedQuantity !== '' && li.issuedQuantity != null
            ? parseFloat(li.issuedQuantity)
            : parseFloat(li.quantity);
          return Number.isFinite(issueQty) && issueQty > 0;
        })
        .map(li => ({
          item: { id: parseInt(li.itemId) },
          quantity: parseFloat(li.quantity),
          unitOfMeasurement: li.unitOfMeasurementId ? { id: parseInt(li.unitOfMeasurementId) } : null,
          issuedQuantity: li.issuedQuantity ? parseFloat(li.issuedQuantity) : parseFloat(li.quantity),
          remarks: li.remarks || '',
          grnLineItem: li.grnLineItemId ? { id: parseInt(li.grnLineItemId) } : null,
          unitPrice: li.unitPrice !== '' && !isNaN(parseFloat(li.unitPrice)) ? parseFloat(li.unitPrice) : null
        }));

      if (lineItems.length === 0) {
        toast.error('Add at least one line with Issue Qty greater than zero');
        return;
      }

      const issuingLocationType = (ginFormData.issuingLocationType || 'WAREHOUSE').toUpperCase();
      const payload = {
        ginNumber: ginFormData.ginNumber || null,
        ginDate: ginFormData.ginDate,
        issuingLocationType,
        warehouse: issuingLocationType === 'WAREHOUSE' && ginFormData.issuingLocationId
          ? { id: parseInt(ginFormData.issuingLocationId) } : null,
        retailStore: issuingLocationType === 'RETAIL_STORE' && ginFormData.issuingLocationId
          ? { id: parseInt(ginFormData.issuingLocationId) } : null,
        sourceProductionUnit: issuingLocationType === 'PRODUCTION_UNIT' && ginFormData.issuingLocationId
          ? { id: parseInt(ginFormData.issuingLocationId) } : null,
        goodsMovementType: ginFormData.movementTypeId ? { id: parseInt(ginFormData.movementTypeId) } : null,
        workOrder: ginFormData.workOrderId ? { id: parseInt(ginFormData.workOrderId) } : null,
        purchaseOrder: ginFormData.purchaseOrderId ? { id: parseInt(ginFormData.purchaseOrderId) } : null,
        stockTransferOrder: ginFormData.stockTransferOrderId ? { id: parseInt(ginFormData.stockTransferOrderId) } : null,
        referenceNumber: ginFormData.referenceNumber || null,
        customerName: ginFormData.customerName || null,
        department: ginFormData.department || null,
        remarks: ginFormData.remarks || null,
        status: ginFormData.status || 'DRAFT',
        lineItems
      };

      if (selectedGIN) {
        await axios.put(`/api/inventory/gins/${selectedGIN.id}`, payload);
        toast.success('GIN updated successfully');
      } else {
        await axios.post('/api/inventory/gins', payload);
        toast.success('GIN created successfully');
      }
      setShowGINModal(false);
      fetchGINs();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving GIN';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving GIN');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'DRAFT': 'secondary',
      'APPROVED': 'primary',
      'RECEIVED': 'info',
      'PARTIAL': 'warning',
      'COMPLETED': 'success',
      'CANCELLED': 'danger',
      'REVERSED': 'dark',
      'ISSUED': 'success',
      'IN_TRANSIT': 'info'
    };
    return <Badge bg={badges[status] || 'secondary'}>{status}</Badge>;
  };

  const filteredStockLevels = stockLevels.filter(stock => {
    if (stockFilterLocation === 'ALL') return true;
    const locationKey = stock.locationType != null && stock.locationId != null
      ? `${stock.locationType}:${stock.locationId}`
      : stock.warehouseId != null ? `WAREHOUSE:${stock.warehouseId}` : null;
    return locationKey === stockFilterLocation;
  });

  if (loading && activeTab === 'stock-levels') {
    return (
      <Container fluid className="mt-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Alerts for Inventory (which ones, and for whom, is in alerts/alertRegistry.js) */}
      <ModuleAlerts module="inventory" section={activeTab} />
      <Row>
        <Col>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
            {/* Stock Levels Tab */}
            <Tab 
              eventKey="stock-levels" 
              title="Stock Levels"
            >
              {showStockTransactions ? (
                // Stock Transactions View
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <Button variant="outline-secondary" onClick={handleBackToStockLevels} className="me-2">
                        ← Back to Stock Levels
                      </Button>
                      <h5 className="d-inline-block mb-0 ms-2">{selectedStockItem?.itemName}</h5>
                    </div>
                  </div>
                  <div className="mb-3">
                    <p className="text-muted mb-0">
                      <strong>Location:</strong> {selectedStockItem?.locationName}
                      {selectedStockItem?.locationType && (
                        <Badge bg="secondary" className="ms-2">
                          {selectedStockItem.locationType === 'WAREHOUSE' ? 'Warehouse' : selectedStockItem.locationType === 'RETAIL_STORE' ? 'Retail Store' : selectedStockItem.locationType === 'IN_TRANSIT' ? 'In Transit' : 'Production Unit'}
                        </Badge>
                      )}
                    </p>
                  </div>
                  <Card>
                    <Card.Body>
                      {stockTransactions.length === 0 ? (
                        <p className="text-muted text-center">No transactions found for this item.</p>
                      ) : (
                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Type</th>
                              <th>Reference</th>
                              <th>Quantity</th>
                              <th>UOM</th>
                              <th>Details</th>
                              <th>Landed Cost</th>
                              <th>Total Landed Cost</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stockTransactions.map((transaction, index) => (
                              <tr key={index}>
                                <td>{formatDate(transaction.date)}</td>
                                <td>
                                  {transaction.type === 'RECEIVED' ? (
                                    <Badge bg="success">Received</Badge>
                                  ) : transaction.type === 'PRODUCED' ? (
                                    <Badge bg="primary">Produced</Badge>
                                  ) : transaction.type === 'CONSUMED' ? (
                                    <Badge bg="danger">Consumed</Badge>
                                  ) : (
                                    <Badge bg="warning">Issued</Badge>
                                  )}
                                </td>
                                <td>{transaction.referenceNumber}</td>
                                <td>{parseFloat(transaction.quantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                                <td>{transaction.unitOfMeasurement?.symbol || transaction.unitOfMeasurement?.name || '-'}</td>
                                <td>
                                  {(transaction.type === 'RECEIVED' || transaction.type === 'PRODUCED') ? (
                                    <div>
                                      {transaction.vendor && <div><small>Vendor: {transaction.vendor.name}</small></div>}
                                      {transaction.vendorLotNumber && <div><small>Vendor Lot: {transaction.vendorLotNumber}</small></div>}
                                      {transaction.internalLotNumber && <div><small>Internal Lot: {transaction.internalLotNumber}</small></div>}
                                    </div>
                                  ) : transaction.type === 'CONSUMED' ? (
                                    <div>
                                      {transaction.remarks && <div><small>{transaction.remarks}</small></div>}
                                    </div>
                                  ) : (
                                    <div>
                                      {transaction.customerName && <div><small>Customer: {transaction.customerName}</small></div>}
                                      {transaction.department && <div><small>Department: {transaction.department}</small></div>}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {transaction.landedCostPerUnit != null ? (
                                    <div>
                                      <div><strong>{formatCurrency(transaction.landedCostPerUnit)}/unit</strong></div>
                                      {((transaction.allocatedServiceCost != null && parseFloat(transaction.allocatedServiceCost) > 0) || 
                                        (transaction.additionalCosts != null && parseFloat(transaction.additionalCosts) > 0)) && (
                                        <div className="mt-1">
                                          {transaction.allocatedServiceCost != null && parseFloat(transaction.allocatedServiceCost) > 0 && (
                                            <div><small className="text-muted">Service: {formatCurrency(transaction.allocatedServiceCost)}</small></div>
                                          )}
                                          {transaction.additionalCosts != null && parseFloat(transaction.additionalCosts) > 0 && (
                                            <div><small className="text-muted">Additional: {formatCurrency(transaction.additionalCosts)}</small></div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  {transaction.landedCost != null ? (
                                    <strong>{formatCurrency(transaction.landedCost)}</strong>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  <Badge bg={transaction.status === 'COMPLETED' || transaction.status === 'RECEIVED' || transaction.status === 'ISSUED' ? 'success' : 'secondary'}>
                                    {transaction.status}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              ) : (
                // Stock Levels View
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="text-muted mb-0">View current stock levels of items at warehouses, retail stores, and production units, plus stock currently in transit between locations</p>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Select
                        style={{ minWidth: '220px' }}
                        value={stockFilterLocation}
                        onChange={(e) => setStockFilterLocation(e.target.value)}
                      >
                        <option value="ALL">All Locations</option>
                        <option value="IN_TRANSIT:0">In Transit</option>
                        <optgroup label="Warehouses">
                          {warehouses.map(wh => (
                            <option key={`W-${wh.id}`} value={`WAREHOUSE:${wh.id}`}>
                              {wh.name}{wh.isActive === false ? ' (Inactive)' : ''}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Retail Stores">
                          {(retailStores || []).map(rs => (
                            <option key={`R-${rs.id}`} value={`RETAIL_STORE:${rs.id}`}>
                              {rs.name}{rs.isActive === false ? ' (Inactive)' : ''}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Production Units">
                          {(productionUnits || []).map(pu => (
                            <option key={`P-${pu.id}`} value={`PRODUCTION_UNIT:${pu.id}`}>
                              {pu.name}{pu.isActive === false ? ' (Inactive)' : ''}
                            </option>
                          ))}
                        </optgroup>
                      </Form.Select>
                      <Button variant="outline-secondary" onClick={fetchStockLevels}>
                        Refresh
                      </Button>
                    </div>
                  </div>

                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Location</th>
                            <th>Location Type</th>
                            <th>Item</th>
                            <th>Kind</th>
                            <th>Quantity</th>
                            <th>Reserved</th>
                            <th>Available</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredStockLevels.map((stock, index) => {
                            const locName = stock.locationName ?? stock.warehouseName ?? stock.retailStoreName ?? stock.productionUnitName ?? '-';
                            const locType = stock.locationType ?? 'WAREHOUSE';
                            const locId = stock.locationId ?? stock.warehouseId ?? stock.retailStoreId ?? stock.productionUnitId;
                            const isInactive = stock.locationIsActive === false || stock.warehouseIsActive === false;
                            return (
                              <tr key={`${locType}-${locId}-${stock.itemId}-${index}`}>
                                <td>
                                  {locName}
                                  {isInactive && (
                                    <Badge bg="secondary" className="ms-2">Inactive</Badge>
                                  )}
                                </td>
                                <td>
                                  <Badge
                                    bg={locType === 'WAREHOUSE' ? 'info' : locType === 'RETAIL_STORE' ? 'success' : locType === 'IN_TRANSIT' ? 'secondary' : 'warning'}
                                    className="ms-2"
                                  >
                                    {locType === 'WAREHOUSE' ? 'Warehouse' : locType === 'RETAIL_STORE' ? 'Retail Store' : locType === 'IN_TRANSIT' ? 'In Transit' : 'Production Unit'}
                                  </Badge>
                                </td>
                                <td>{stock.itemName}</td>
                                <td>
                                  <Badge bg={stock.itemSource === 'PRODUCT' ? 'primary' : 'secondary'}>
                                    {stock.itemSource === 'PRODUCT' ? 'Finished Good' : 'Raw Material'}
                                  </Badge>
                                </td>
                                <td>{parseFloat(stock.quantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}{stock.uom ? ` ${stock.uom}` : ''}</td>
                                <td>
                                  {stock.reservedQuantity && parseFloat(stock.reservedQuantity) > 0
                                    ? `${parseFloat(stock.reservedQuantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}${stock.uom ? ` ${stock.uom}` : ''}`
                                    : '-'}
                                </td>
                                <td>
                                  {stock.availableQuantity != null
                                    ? `${parseFloat(stock.availableQuantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}${stock.uom ? ` ${stock.uom}` : ''}`
                                    : '-'}
                                </td>
                                <td>
                                  {locType !== 'IN_TRANSIT' && (
                                    <Button
                                      size="sm"
                                      variant="outline-info"
                                      onClick={() => fetchStockTransactions(locType, locId, stock.itemId, stock.itemName, locName)}
                                      title="View Stock Transactions"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                        <path d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"/>
                                      </svg>
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {filteredStockLevels.length === 0 && (
                            <tr>
                              <td colSpan="8" className="text-center text-muted">
                                No stock levels found. Create GRNs to receive items into warehouses, retail stores, or production units.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </div>
              )}
            </Tab>

            {/* Stock Transfer Orders Tab */}
            <Tab 
              eventKey="stock-transfer-orders" 
              title="Stock Transfer Orders"
            >
              <FeatureNotice feature="STOCK_TRANSFERS" />
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Create and manage stock transfer orders from one location (warehouse, retail store, production unit) to another</p>
                  <Button variant="primary" onClick={handleCreateStockTransferOrder}>
                    + Create Stock Transfer Order
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Transfer Number</th>
                            <th>Date</th>
                            <th>Source</th>
                            <th>Destination</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockTransferOrders.map((order) => (
                            <tr key={order.id}>
                              <td>{order.transferNumber}</td>
                              <td>{formatDate(order.transferDate)}</td>
                              <td>{getStockTransferOrderLocationDisplay(order, 'source')}</td>
                              <td>{getStockTransferOrderLocationDisplay(order, 'destination')}</td>
                              <td>{getStatusBadge(order.status)}</td>
                              <td>
                                <span title="View & Print" style={{ display: 'inline-block' }}>
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    className="me-2"
                                    onClick={() => handleViewPrint(order, 'STO')}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                      <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <span title="Transactions" style={{ display: 'inline-block' }}>
                                  <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => handleViewSTOTransactions(order)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <span title="Edit" style={{ display: 'inline-block' }}>
                                  <Button size="sm" variant="outline-primary" className="me-2" onClick={() => handleEditStockTransferOrder(order)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                      <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                    </svg>
                                  </Button>
                                </span>
                                {canMoveStock && gstEnabled.ewayEnabled && order.status !== 'DRAFT' && (
                                  <Button size="sm" variant="outline-secondary" className="me-2" title="E-way bill for this transfer"
                                    onClick={() => setEwayTransfer(order)}>e-Way</Button>
                                )}
                                {order.status === 'DRAFT' && (
                                  <span title="Delete" style={{ display: 'inline-block' }}>
                                    <Button size="sm" variant="outline-danger" onClick={() => handleDeleteStockTransferOrder(order.id)}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                      </svg>
                                    </Button>
                                  </span>
                                )}
                                <HistoryButton documentType="StockTransferOrder" documentId={order.id} label={`Transfer ${order.transferNumber}`} className="ms-1" />
                              </td>
                            </tr>
                          ))}
                          {stockTransferOrders.length === 0 && (
                            <tr>
                              <td colSpan="6" className="text-center text-muted">
                                No stock transfer orders found. Create one to get started.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab>

            {/* GRN Tab */}
            <Tab 
              eventKey="grns" 
              title="Goods Receipt Notes"
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Manage goods receipt notes when items are received at a warehouse, retail store, or production unit</p>
                <Button variant="primary" onClick={handleCreateGRN}>
                  + Create GRN
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <Card>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>GRN Number</th>
                          <th>Date</th>
                          <th>Receive Location</th>
                          <th>Reference Order</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grns.map((grn) => (
                          <tr key={grn.id}>
                            <td>{grn.grnNumber}</td>
                            <td>{formatDate(grn.grnDate)}</td>
                            <td>{formatGrnReceiveLocation(grn)}</td>
                            <td>
                              {grn.purchaseOrder?.poNumber
                                ? `PO: ${grn.purchaseOrder.poNumber}`
                                : grn.workOrder?.workOrderNumber
                                  ? `WO: ${grn.workOrder.workOrderNumber}`
                                  : grn.stockTransferOrder?.transferNumber
                                    ? `STO: ${grn.stockTransferOrder.transferNumber}`
                                    : '-'}
                            </td>
                            <td>{getStatusBadge(grn.status)}</td>
                            <td>
                              <span title="View & Print" style={{ display: 'inline-block' }}>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(grn, 'GRN')}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                              </span>
                              {(grn.status === 'DRAFT' || grn.status === 'PARTIAL') && (
                                <span title="Edit" style={{ display: 'inline-block' }}>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="me-2"
                                    onClick={() => handleEditGRN(grn)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                      <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                    </svg>
                                  </Button>
                                </span>
                              )}
                              {grn.status === 'RECEIVED' && (
                                <span title="Reverse GRN" style={{ display: 'inline-block' }}>
                                  <Button
                                    size="sm"
                                    variant="outline-warning"
                                    className="me-2"
                                    onClick={() => handleReverseGRN(grn)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path fillRule="evenodd" d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2v1z"/>
                                      <path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466z"/>
                                    </svg>
                                  </Button>
                                </span>
                              )}
                              {grn.status === 'DRAFT' && (
                                <span title="Delete" style={{ display: 'inline-block' }}>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDeleteGRN(grn.id)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </Button>
                                </span>
                              )}
                              <HistoryButton documentType="GoodsReceiptNote" documentId={grn.id} label={`GRN ${grn.grnNumber || grn.id}`} className="ms-1" />
                            </td>
                          </tr>
                        ))}
                        {grns.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center text-muted">
                              No GRNs found. Create one to get started.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab>

            {/* GIN Tab */}
            <Tab 
              eventKey="gins" 
              title="Goods Issue Notes"
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Manage goods issue notes when items are issued from a warehouse, retail store, or production unit</p>
                <Button variant="primary" onClick={handleCreateGIN}>
                  + Create GIN
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <Card>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>GIN Number</th>
                          <th>Date</th>
                          <th>Issuing Location</th>
                          <th>Customer/Dept</th>
                          <th>Reference</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gins.map((gin) => (
                          <tr key={gin.id}>
                            <td>{gin.ginNumber}</td>
                            <td>{formatDate(gin.ginDate)}</td>
                            <td>{formatGinIssueLocation(gin)}</td>
                            <td>{gin.customerName || gin.department || '-'}</td>
                            <td>
                              {gin.workOrder?.workOrderNumber
                                ? `WO: ${gin.workOrder.workOrderNumber}`
                                : gin.purchaseOrder?.poNumber
                                  ? `PO: ${gin.purchaseOrder.poNumber}`
                                  : gin.stockTransferOrder?.transferNumber
                                    ? `STO: ${gin.stockTransferOrder.transferNumber}`
                                    : gin.saleBill?.billNumber
                                      ? `Bill: ${gin.saleBill.billNumber}`
                                      : gin.referenceNumber || '-'}
                            </td>
                            <td>{getStatusBadge(gin.status)}</td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-info"
                                className="me-2"
                                onClick={() => handleViewPrint(gin, 'GIN')}
                                title="View & Print"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                  <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                  <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                </svg>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="me-2"
                                onClick={() => handleEditGIN(gin)}
                                title="Edit"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                  <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                </svg>
                              </Button>
                              {gin.status === 'DRAFT' && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteGIN(gin.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              )}
                              <HistoryButton documentType="GoodsIssueNote" documentId={gin.id} label={`GIN ${gin.ginNumber || gin.id}`} className="ms-1" />
                            </td>
                          </tr>
                        ))}
                        {gins.length === 0 && (
                          <tr>
                            <td colSpan="7" className="text-center text-muted">
                              No GINs found. Create one to get started.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab>
            {canMoveStock && (
              <Tab
                eventKey="stock-adjustments"
                title="Stock Adjustments"
              >
                <StockAdjustments
                  active={activeTab === 'stock-adjustments'}
                  canPost={canAdjustStock}
                  currency={organizationCurrency}
                  locations={[
                    ...warehouses.filter((w) => w.isActive !== false).map((w) => ({ type: 'WAREHOUSE', id: w.id, name: w.name })),
                    ...retailStores.filter((r) => r.isActive !== false).map((r) => ({ type: 'RETAIL_STORE', id: r.id, name: `${r.name} (store)` })),
                    ...productionUnits.filter((u) => u.isActive !== false).map((u) => ({ type: 'PRODUCTION_UNIT', id: u.id, name: `${u.name} (production)` }))
                  ]}
                />
              </Tab>
            )}
            {canAdjustStock && (
              <Tab
                eventKey="stock-ageing"
                title="Stock Ageing"
              >
                <StockAgeing active={activeTab === 'stock-ageing'} currency={organizationCurrency} />
              </Tab>
            )}
          </Tabs>
        </Col>
      </Row>

      {/* Stock Transfer Order Modal */}
      <Modal show={showStockTransferOrderModal} onHide={() => setShowStockTransferOrderModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedStockTransferOrder ? 'Edit Stock Transfer Order' : 'Create Stock Transfer Order'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveStockTransferOrder}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Transfer Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={stockTransferOrderFormData.transferNumber}
                    onChange={(e) => setStockTransferOrderFormData({ ...stockTransferOrderFormData, transferNumber: e.target.value })}
                    placeholder="Auto-generated if left empty"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Transfer Date *</Form.Label>
                  <DateInput
                   
                    value={stockTransferOrderFormData.transferDate}
                    onChange={(e) => setStockTransferOrderFormData({ ...stockTransferOrderFormData, transferDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={stockTransferOrderFormData.status}
                    onChange={(e) => setStockTransferOrderFormData({ ...stockTransferOrderFormData, status: e.target.value })}
                  >
                    <option value="DRAFT">DRAFT</option>
                    <option value="APPROVED">Approved</option>
                    <option value="IN_TRANSIT">IN_TRANSIT</option>
                    <option value="COMPLETED">COMPLETED</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Location *</Form.Label>
                  <Form.Select
                    value={`${stockTransferOrderFormData.sourceLocationType}:${stockTransferOrderFormData.sourceLocationId}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        setStockTransferOrderFormData(prev => ({ ...prev, sourceLocationType: 'WAREHOUSE', sourceLocationId: '' }));
                        return;
                      }
                      const [type, id] = v.split(':');
                      setStockTransferOrderFormData(prev => ({ ...prev, sourceLocationType: type || 'WAREHOUSE', sourceLocationId: id || '' }));
                    }}
                    required
                  >
                    <option value="">Select source location</option>
                    {warehouses.filter(w => w.isActive).length > 0 && (
                      <optgroup label="Warehouses">
                        {warehouses.filter(w => w.isActive).map(w => (
                          <option key={`WH:${w.id}`} value={`WAREHOUSE:${w.id}`}>{w.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {retailStores.filter(r => r.isActive).length > 0 && (
                      <optgroup label="Retail Stores">
                        {retailStores.filter(r => r.isActive).map(r => (
                          <option key={`RS:${r.id}`} value={`RETAIL_STORE:${r.id}`}>{r.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {productionUnits.filter(p => p.isActive).length > 0 && (
                      <optgroup label="Production Units">
                        {productionUnits.filter(p => p.isActive).map(p => (
                          <option key={`PU:${p.id}`} value={`PRODUCTION_UNIT:${p.id}`}>{p.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Destination Location *</Form.Label>
                  <Form.Select
                    value={`${stockTransferOrderFormData.destinationLocationType}:${stockTransferOrderFormData.destinationLocationId}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        setStockTransferOrderFormData(prev => ({ ...prev, destinationLocationType: 'WAREHOUSE', destinationLocationId: '' }));
                        return;
                      }
                      const [type, id] = v.split(':');
                      setStockTransferOrderFormData(prev => ({ ...prev, destinationLocationType: type || 'WAREHOUSE', destinationLocationId: id || '' }));
                    }}
                    required
                  >
                    <option value="">Select destination location</option>
                    {warehouses.filter(w => w.isActive).length > 0 && (
                      <optgroup label="Warehouses">
                        {warehouses.filter(w => w.isActive).map(w => (
                          <option key={`WH:${w.id}`} value={`WAREHOUSE:${w.id}`}>{w.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {retailStores.filter(r => r.isActive).length > 0 && (
                      <optgroup label="Retail Stores">
                        {retailStores.filter(r => r.isActive).map(r => (
                          <option key={`RS:${r.id}`} value={`RETAIL_STORE:${r.id}`}>{r.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {productionUnits.filter(p => p.isActive).length > 0 && (
                      <optgroup label="Production Units">
                        {productionUnits.filter(p => p.isActive).map(p => (
                          <option key={`PU:${p.id}`} value={`PRODUCTION_UNIT:${p.id}`}>{p.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={stockTransferOrderFormData.remarks}
                onChange={(e) => setStockTransferOrderFormData({ ...stockTransferOrderFormData, remarks: e.target.value })}
              />
            </Form.Group>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Line Items *</Form.Label>
                <Button type="button" size="sm" variant="outline-primary" onClick={addStockTransferOrderLineItem}>+ Add Item</Button>
              </div>
              {stockTransferOrderLineItems.map((li, idx) => (
                <Card key={idx} className="mb-2">
                  <Card.Body className="py-2">
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small">Item *</Form.Label>
                          <Form.Select
                            value={li.itemId}
                            onChange={(e) => updateStockTransferOrderLineItem(idx, 'itemId', e.target.value)}
                            required
                          >
                            <option value="">Select item</option>
                            {items.filter(i => i.isActive).map(item => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small">UOM</Form.Label>
                          <Form.Select
                            value={li.unitOfMeasurementId}
                            onChange={(e) => updateStockTransferOrderLineItem(idx, 'unitOfMeasurementId', e.target.value)}
                          >
                            <option value="">—</option>
                            {uoms.map(u => (
                              <option key={u.id} value={u.id}>{u.symbol || u.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small">Quantity *</Form.Label>
                          <Form.Control
                            type="number"
                            step="any"
                            min="0"
                            value={li.quantity}
                            onChange={(e) => updateStockTransferOrderLineItem(idx, 'quantity', e.target.value)}
                            placeholder="0"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small">Remarks</Form.Label>
                          <Form.Control
                            type="text"
                            value={li.remarks}
                            onChange={(e) => updateStockTransferOrderLineItem(idx, 'remarks', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={1} className="d-flex align-items-end">
                        <Button type="button" size="sm" variant="outline-danger" onClick={() => removeStockTransferOrderLineItem(idx)} title="Remove">×</Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowStockTransferOrderModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">{selectedStockTransferOrder ? 'Update' : 'Create'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Stock Transfer Order Transactions Modal (GINs and GRNs against this STO) */}
      <Modal show={showSTOTransactionsModal} onHide={() => { setShowSTOTransactionsModal(false); setSelectedSTOForTransactions(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Transactions — {selectedSTOForTransactions?.transferNumber || 'Stock Transfer Order'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingSTOTransactions ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner>
            </div>
          ) : (
            <>
              <h6 className="mb-2">Goods Issue Notes (GINs) — issued against this STO</h6>
              {stoTransactions.gins && stoTransactions.gins.length > 0 ? (
                <Table size="sm" striped bordered className="mb-4">
                  <thead>
                    <tr>
                      <th>GIN Number</th>
                      <th>Date</th>
                      <th>Location</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stoTransactions.gins.map((g) => (
                      <tr key={`gin-${g.id}`}>
                        <td>{g.ginNumber || '-'}</td>
                        <td>{formatDate(g.ginDate) || '-'}</td>
                        <td>{g.locationName || '-'}</td>
                        <td>{g.status ? <Badge bg={g.status === 'ISSUED' ? 'success' : 'secondary'}>{g.status}</Badge> : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted mb-4">No GINs issued against this Stock Transfer Order.</p>
              )}
              <h6 className="mb-2">Goods Receipt Notes (GRNs) — received against this STO</h6>
              {stoTransactions.grns && stoTransactions.grns.length > 0 ? (
                <Table size="sm" striped bordered>
                  <thead>
                    <tr>
                      <th>GRN Number</th>
                      <th>Date</th>
                      <th>Location</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stoTransactions.grns.map((g) => (
                      <tr key={`grn-${g.id}`}>
                        <td>{g.grnNumber || '-'}</td>
                        <td>{formatDate(g.grnDate) || '-'}</td>
                        <td>{g.locationName || '-'}</td>
                        <td>{g.status ? <Badge bg={g.status === 'RECEIVED' || g.status === 'COMPLETED' ? 'success' : 'secondary'}>{g.status}</Badge> : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No GRNs received against this Stock Transfer Order.</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowSTOTransactionsModal(false); setSelectedSTOForTransactions(null); }}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* GRN Modal */}
      <Modal show={showGRNModal} onHide={() => setShowGRNModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{selectedGRN ? 'Edit GRN' : 'Create GRN'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveGRN}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>GRN Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={grnFormData.grnNumber}
                    onChange={(e) => setGrnFormData({ ...grnFormData, grnNumber: e.target.value })}
                    placeholder="Auto-generated if left empty"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>GRN Date *</Form.Label>
                  <DateInput
                   
                    value={grnFormData.grnDate}
                    onChange={(e) => setGrnFormData({ ...grnFormData, grnDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Movement Type *</Form.Label>
                  <Form.Select
                    value={grnFormData.movementTypeId}
                    onChange={(e) => handleMovementTypeChange(e.target.value)}
                    required
                  >
                    <option value="">Select Movement Type</option>
                    {inboundMovementTypes.filter(mt => mt.isActive).map(mt => (
                      <option key={mt.id} value={mt.id}>{mt.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Inbound movement types (e.g., Receive from Vendor, Receive from Production)</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              {/* Receiving Location - restricted to the selected Movement Type's locationType, when set */}
              {(() => {
                const selectedMovementTypeForLocation = inboundMovementTypes.find(mt => {
                  const mtId = typeof mt.id === 'string' ? parseInt(mt.id) : mt.id;
                  const formId = typeof grnFormData.movementTypeId === 'string' ? parseInt(grnFormData.movementTypeId) : grnFormData.movementTypeId;
                  return mtId === formId;
                });
                const restrictToType = selectedMovementTypeForLocation?.locationType || null;
                return (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Receiving Location *</Form.Label>
                      <LocationSelect
                        warehouses={warehouses}
                        retailStores={retailStores}
                        productionUnits={productionUnits}
                        restrictToType={restrictToType}
                        value={
                          grnFormData.receivingLocationType === 'RETAIL_STORE'
                            ? `RETAIL_STORE:${grnFormData.receivingLocationId || ''}`
                            : grnFormData.receivingLocationType === 'PRODUCTION_UNIT'
                              ? `PRODUCTION_UNIT:${grnFormData.receivingLocationId || ''}`
                              : (grnFormData.warehouseId ? `WAREHOUSE:${grnFormData.warehouseId}` : '')
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value) {
                            setGrnFormData({
                              ...grnFormData,
                              warehouseId: '',
                              receivingLocationType: 'WAREHOUSE',
                              receivingLocationId: '',
                              purchaseOrderId: '',
                              workOrderId: ''
                            });
                          } else {
                            const [locType, id] = value.split(':');
                            const normalizedType = (locType || 'WAREHOUSE').toUpperCase();
                            setGrnFormData({
                              ...grnFormData,
                              warehouseId: normalizedType === 'WAREHOUSE' ? id : '',
                              receivingLocationType: normalizedType,
                              receivingLocationId: id,
                              purchaseOrderId: '',
                              workOrderId: ''
                            });
                          }
                          // Clear line items when warehouse changes
                          const newInternalLotNumber = generateInternalLotNumber();
                          setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
                          setGrnServiceItems([]);
                        }}
                      />
                    </Form.Group>
                  </Col>
                );
              })()}
              {/* Reference Order - dynamically changes based on Movement Type */}
              {(() => {
                // Find the selected movement type - handle both string and number ID comparisons
                const selectedMovementType = inboundMovementTypes.find(mt => {
                  const mtId = typeof mt.id === 'string' ? parseInt(mt.id) : mt.id;
                  const formId = typeof grnFormData.movementTypeId === 'string' ? parseInt(grnFormData.movementTypeId) : grnFormData.movementTypeId;
                  return mtId === formId;
                });

                // Get referenceType from the movement type, default to PURCHASE_ORDER
                let referenceType = 'PURCHASE_ORDER';
                if (selectedMovementType) {
                  referenceType = selectedMovementType.referenceType || 'PURCHASE_ORDER';
                  // Normalize to uppercase for consistency
                  referenceType = referenceType.toUpperCase();
                  
                  // Fallback: If referenceType is not set, try to infer from movement type name
                  if (!selectedMovementType.referenceType || selectedMovementType.referenceType.trim() === '') {
                    const name = (selectedMovementType.name || '').toUpperCase();
                    if (name.includes('PRODUCTION') || name.includes('WORK ORDER') || name.includes('WORKORDER')) {
                      referenceType = 'WORK_ORDER';
                    } else if (name.includes('TRANSFER') || name.includes('STOCK TRANSFER')) {
                      referenceType = 'STOCK_TRANSFER_ORDER';
                    } else if (name.includes('PROCUREMENT') || name.includes('FIELD SLIP') || name.includes('SURVEY')) {
                      referenceType = 'PROCUREMENT_SURVEY';
                    } else if (name.includes('SALE') || name.includes('RETURN')) {
                      referenceType = 'SALES_ORDER';
                    }
                  }
                }
                
                // Get the label for the reference order based on referenceType
                const getReferenceOrderLabel = (refType) => {
                  switch (refType) {
                    case 'WORK_ORDER':
                      return 'Work Order';
                    case 'SALES_ORDER':
                      return 'Sales Order';
                    case 'STOCK_TRANSFER_ORDER':
                      return 'Stock Transfer Order';
                    case 'PROCUREMENT_SURVEY':
                      return 'Procurement Survey';
                    case 'PURCHASE_ORDER':
                    default:
                      return 'Purchase Order';
                  }
                };
                
                const referenceOrderLabel = getReferenceOrderLabel(referenceType);
                
                    if (referenceType === 'WORK_ORDER') {
                      const isWarehouseLocation = (grnFormData.receivingLocationType || 'WAREHOUSE').toUpperCase() === 'WAREHOUSE';
                      const isProductionUnitLocation = (grnFormData.receivingLocationType || '').toUpperCase() === 'PRODUCTION_UNIT';
                      const isWarehouseSelected = isWarehouseLocation && !!grnFormData.warehouseId;
                      const hasProductionUnitSelected = isProductionUnitLocation && !!grnFormData.receivingLocationId;
                      const referenceOrderEnabled = isWarehouseSelected || hasProductionUnitSelected;
                      const baseWorkOrderOptions = hasProductionUnitSelected
                        ? workOrdersIncompleteReceipts
                        : workOrders.filter(wo => wo.status === 'COMPLETED');
                      const selectedWorkOrderId = grnFormData.workOrderId ? parseInt(grnFormData.workOrderId) : null;
                      const hasSelectedInOptions = selectedWorkOrderId != null && baseWorkOrderOptions.some(
                        wo => wo.id === selectedWorkOrderId
                      );
                      const selectedWorkOrderOption = selectedWorkOrderId == null
                        ? null
                        : (workOrders.find(wo => wo.id === selectedWorkOrderId)
                          || (selectedGRN?.workOrder?.id === selectedWorkOrderId ? selectedGRN.workOrder : null));
                      const workOrderOptions = (!hasSelectedInOptions && selectedWorkOrderOption)
                        ? [selectedWorkOrderOption, ...baseWorkOrderOptions]
                        : baseWorkOrderOptions;
                  return (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Order ({referenceOrderLabel}) *</Form.Label>
                        <Form.Select
                          value={grnFormData.workOrderId}
                          onChange={(e) => handleWorkOrderChange(e.target.value)}
                          disabled={!referenceOrderEnabled}
                          required
                        >
                          <option value="">Select {referenceOrderLabel}</option>
                          {workOrderOptions.map(wo => (
                            <option key={wo.id} value={wo.id}>
                              {wo.workOrderNumber} {wo.workOrderDate ? `(${formatDate(wo.workOrderDate)})` : ''} - {wo.bom?.bomNumber || ''}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {!referenceOrderEnabled
                            ? (hasProductionUnitSelected ? '' : 'Select a receiving location first (Warehouse or Production Unit)')
                            : hasProductionUnitSelected
                              ? 'Work orders at this production unit with items issued but not all quantity received yet'
                              : `${referenceOrderLabel}s with COMPLETED status`}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  );
                } else if (referenceType === 'STOCK_TRANSFER_ORDER') {
                  const locType = (grnFormData.receivingLocationType || 'WAREHOUSE').toUpperCase();
                  const hasReceivingLocation = (locType === 'WAREHOUSE' && !!grnFormData.warehouseId)
                    || (locType === 'RETAIL_STORE' && !!grnFormData.receivingLocationId)
                    || (locType === 'PRODUCTION_UNIT' && !!grnFormData.receivingLocationId);
                  const filteredSTOs = stockTransferOrders.filter(sto => {
                    // A GRN receives what a GIN already issued — only IN_TRANSIT STOs have anything
                    // issued and not yet fully received. Keep the currently-selected STO visible on edit
                    // even if it has since moved on (e.g. to COMPLETED).
                    const isCurrentSelection = grnFormData.stockTransferOrderId
                      && String(sto.id) === String(grnFormData.stockTransferOrderId);
                    if (sto.status !== 'IN_TRANSIT' && !isCurrentSelection) return false;
                    if (!hasReceivingLocation) return true;
                    if (locType === 'WAREHOUSE') {
                      return sto.destinationLocationType === 'WAREHOUSE' && sto.destinationWarehouse?.id === parseInt(grnFormData.warehouseId);
                    }
                    if (locType === 'RETAIL_STORE') {
                      return sto.destinationLocationType === 'RETAIL_STORE' && sto.destinationRetailStore?.id === parseInt(grnFormData.receivingLocationId);
                    }
                    if (locType === 'PRODUCTION_UNIT') {
                      return sto.destinationLocationType === 'PRODUCTION_UNIT' && sto.destinationProductionUnit?.id === parseInt(grnFormData.receivingLocationId);
                    }
                    return false;
                  });
                  return (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Order ({referenceOrderLabel}) *</Form.Label>
                        <Form.Select
                          value={grnFormData.stockTransferOrderId}
                          onChange={(e) => handleGRNStockTransferOrderChange(e.target.value)}
                          disabled={!hasReceivingLocation}
                          required
                        >
                          <option value="">Select {referenceOrderLabel}</option>
                          {filteredSTOs.map(sto => (
                            <option key={sto.id} value={sto.id}>
                              {sto.transferNumber} {sto.transferDate ? `(${formatDate(sto.transferDate)})` : ''} - {sto.status}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {!hasReceivingLocation ? 'Select receiving location first' : 'In Transit Stock Transfer Orders (already issued from source) filtered by destination'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  );
                } else if (referenceType === 'SALES_ORDER') {
                  // Placeholder for future Sales Order implementation
                  return (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Order ({referenceOrderLabel})</Form.Label>
                        <Form.Select
                          value=""
                          disabled={true}
                        >
                          <option value="">Sales Order support coming soon</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Sales Order reference will be available in a future update
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  );
                } else {
                  const isWarehouseLocation = (grnFormData.receivingLocationType || 'WAREHOUSE').toUpperCase() === 'WAREHOUSE';
                  const isWarehouseSelected = isWarehouseLocation && !!grnFormData.warehouseId;
                  const surveyRequired = referenceType === 'PROCUREMENT_SURVEY';
                  return (
                    <>
                      {!surveyRequired && (
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Purchase Order</Form.Label>
                            <Form.Select
                              value={grnFormData.purchaseOrderId}
                              onChange={(e) => handlePurchaseOrderChange(e.target.value)}
                              disabled={!isWarehouseSelected || !!grnFormData.procurementSurveyId}
                            >
                              <option value="">Select Purchase Order (optional)</option>
                              {purchaseOrders
                                .filter(po => !isWarehouseSelected || po.warehouse?.id === parseInt(grnFormData.warehouseId))
                                .map(po => (
                                  <option key={po.id} value={po.id}>
                                    {po.poNumber} {po.poDate ? `(${formatDate(po.poDate)})` : ''}
                                  </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                              {!isWarehouseSelected
                                ? 'Select a warehouse first'
                                : 'Optional — or use a New/In Progress Procurement Survey instead'}
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      )}
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Procurement Survey{surveyRequired ? ' *' : ''}</Form.Label>
                          <Form.Select
                            value={grnFormData.procurementSurveyId}
                            onChange={(e) => handleProcurementSurveyChange(e.target.value)}
                            disabled={!isWarehouseSelected || (!!grnFormData.purchaseOrderId && !surveyRequired)}
                            required={surveyRequired}
                          >
                            <option value="">{surveyRequired ? 'Select Survey *' : 'Select Survey (optional)'}</option>
                            {approvedProcurementSurveys.map(s => (
                              <option key={s.id} value={s.id}>
                                {s.surveyNumber} {s.surveyDate ? `(${formatDate(s.surveyDate)})` : ''}
                                {s.status === 'COMPLETED' ? ' [Completed]' : ''}
                                {' — '}{s.item?.name || 'Material?'} — {s.vendor?.name || ''}
                                {s.siteName || s.city ? ` / ${[s.siteName, s.city].filter(Boolean).join(', ')}` : ''}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            {!isWarehouseSelected
                              ? 'Select a warehouse first'
                              : surveyRequired
                                ? 'Required — New, In Progress, or Completed field slips from Purchase → Procurement Surveys'
                                : 'New, In Progress, or Completed surveys from Purchase → Procurement Surveys'}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </>
                  );
                }
              })()}
              {/* Status field */}
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={grnFormData.status}
                    onChange={(e) => setGrnFormData({ ...grnFormData, status: e.target.value })}
                  >
                    <option value="DRAFT">DRAFT</option>
                    <option value="PARTIAL">PARTIAL</option>
                    <option value="RECEIVED">RECEIVED</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Once set to RECEIVED, the GRN is locked. Delete a DRAFT to discard it, or use "Reverse" to correct a received receipt.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            {!grnFormData.workOrderId && !grnFormData.stockTransferOrderId && (!selectedGRN || selectedGRN.status === 'DRAFT') && (
              <GrnBillChoice
                vendorIds={grnLineItems.map((li) => li.vendorId)}
                value={grnFormData}
                onChange={(v) => setGrnFormData((prev) => ({ ...prev, ...v }))}
                onUseBillGoods={fillGrnLinesFromBill}
              />
            )}
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={grnFormData.remarks}
                onChange={(e) => setGrnFormData({ ...grnFormData, remarks: e.target.value })}
              />
            </Form.Group>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Line Items *</Form.Label>
                <Button size="sm" variant="outline-primary" onClick={handleAddGRNLineItem}>
                  + Add Item
                </Button>
              </div>
              {grnLineItems.map((lineItem, index) => {
                const isProductionUnitReceive = (grnFormData.receivingLocationType || '').toUpperCase() === 'PRODUCTION_UNIT';
                const internalStoGrn = isGrnInternalStockTransfer();
                return (
                <Card key={index} className="mb-2">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div className="flex-grow-1 min-w-0">
                        {(lineItem.orderedQuantity || lineItem.receivedQuantityTotal || lineItem.pendingQuantity) && (
                          <Alert variant="info" className="mb-0 py-2">
                            <small>
                              <strong>{lineItem.isFromWO ? 'WO Summary' : 'PO Summary'}:</strong> {lineItem.isFromWO ? 'Expected' : 'Ordered'}: {parseFloat(lineItem.orderedQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{lineItem.uomSymbol ? ` ${lineItem.uomSymbol}` : ''} | 
                              Already Received: {parseFloat(lineItem.receivedQuantityTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{lineItem.uomSymbol ? ` ${lineItem.uomSymbol}` : ''} | 
                              Pending: {parseFloat(lineItem.pendingQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{lineItem.uomSymbol ? ` ${lineItem.uomSymbol}` : ''}
                            </small>
                          </Alert>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="flex-shrink-0"
                        onClick={() => handleRemoveGRNLineItem(index)}
                        disabled={grnLineItems.length === 1}
                        title="Remove line"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                          <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                        </svg>
                      </Button>
                    </div>
                    <Row className="g-2">
                      <Col md={isProductionUnitReceive || internalStoGrn ? 4 : 2}>
                        <Form.Group>
                          <Form.Label className="text-nowrap">Item *</Form.Label>
                          <Form.Select
                            value={lineItem.itemId}
                            onChange={(e) => handleGRNLineItemChange(index, 'itemId', e.target.value)}
                            required
                            disabled={lineItem.isFromPO || (lineItem.isFromWO && (grnFormData.receivingLocationType || '').toUpperCase() !== 'PRODUCTION_UNIT')}
                          >
                            <option value="">Select Item</option>
                            {(() => {
                              const selectedMovementType = inboundMovementTypes.find(mt => mt.id === parseInt(grnFormData.movementTypeId));
                              const referenceType = (selectedMovementType?.referenceType || '').toUpperCase();
                              const movementName = (selectedMovementType?.name || '').toUpperCase();
                              const isReceiveAtProductionUnit = (grnFormData.receivingLocationType || '').toUpperCase() === 'PRODUCTION_UNIT';
                              const isProductionReceipt = referenceType === 'WORK_ORDER' || movementName.includes('PRODUCTION UNIT');

                              if (isProductionReceipt && isReceiveAtProductionUnit) {
                                return items.filter(i => i.isActive).map(item => (
                                  <option key={item.id} value={item.id}>{item.name}</option>
                                ));
                              }
                              if (isProductionReceipt) {
                                return products
                                  .filter(p => p.isActive && p.item && p.item.isActive)
                                  .map(product => (
                                    <option key={product.item.id} value={product.item.id}>
                                      {product.item.name}{product.name && product.name !== product.item.name ? ` (${product.name})` : ''}
                                    </option>
                                  ));
                              }

                              return items.filter(i => i.isActive).map(item => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                              ));
                            })()}
                          </Form.Select>
                          {(() => {
                            const selectedMovementType = inboundMovementTypes.find(mt => mt.id === parseInt(grnFormData.movementTypeId));
                            const referenceType = (selectedMovementType?.referenceType || '').toUpperCase();
                            const movementName = (selectedMovementType?.name || '').toUpperCase();
                            const isReceiveAtProductionUnit = (grnFormData.receivingLocationType || '').toUpperCase() === 'PRODUCTION_UNIT';
                            const isProductionReceipt = referenceType === 'WORK_ORDER' || movementName.includes('PRODUCTION UNIT');
                            if (!isProductionReceipt) return null;
                            if (isReceiveAtProductionUnit) {
                              return (
                                <Form.Text className="text-muted">
                                  Raw materials (items) issued by GIN for this Work Order.
                                </Form.Text>
                              );
                            }
                            return (
                              <Form.Text className="text-muted">
                                Only products from Org Admin - Products are allowed.
                              </Form.Text>
                            );
                          })()}
                        </Form.Group>
                      </Col>
                      {(() => {
                        const refUpper = getGrnFormReferenceTypeUpper();
                        // Hide Vendor for work order and internal stock transfer GRNs
                        if (refUpper === 'WORK_ORDER' || refUpper === 'STOCK_TRANSFER_ORDER') {
                          return null;
                        }
                        
                        return (
                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="text-nowrap">Vendor</Form.Label>
                              <Form.Select
                                value={lineItem.vendorId}
                                onChange={(e) => handleGRNLineItemChange(index, 'vendorId', e.target.value)}
                                disabled={lineItem.isFromPO} // Read-only for items from PO
                              >
                                <option value="">Select Vendor</option>
                                {vendors
                                  .filter(v => v.isActive && (!v.vendorType || 
                                    (!v.vendorType.name.toUpperCase().includes('H&T') && 
                                     !v.vendorType.name.toUpperCase().includes('TRANSPORTER'))))
                                  .map(vendor => (
                                    <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                                  ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        );
                      })()}
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="text-nowrap">UOM</Form.Label>
                          <Form.Select
                            value={lineItem.unitOfMeasurementId}
                            onChange={(e) => handleGRNLineItemChange(index, 'unitOfMeasurementId', e.target.value)}
                            disabled={lineItem.isFromPO || (lineItem.isFromWO && (grnFormData.receivingLocationType || '').toUpperCase() !== 'PRODUCTION_UNIT')}
                          >
                            <option value="">Select UOM</option>
                            {uoms.map(uom => (
                              <option key={uom.id} value={uom.id}>{uom.name} ({uom.symbol})</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={isProductionUnitReceive || internalStoGrn ? 3 : 2}>
                        <Form.Group>
                          <Form.Label className="text-nowrap">Received Qty *</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={lineItem.receivedQuantity}
                            onChange={(e) => handleGRNLineItemChange(index, 'receivedQuantity', e.target.value)}
                            required
                            disabled={lineItem.isFromPO && lineItem.weighBridgeReceiptId} // Disabled if from PO and weigh bridge receipt is selected
                          />
                          {lineItem.pendingQuantity && (
                            <Form.Text className="text-muted">
                              Remaining: {parseFloat(lineItem.pendingQuantity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{lineItem.uomSymbol ? ` ${lineItem.uomSymbol}` : ''}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      {!isProductionUnitReceive && !internalStoGrn && (
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label className="text-nowrap">Unit Price</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              value={lineItem.unitPrice}
                              onChange={(e) => handleGRNLineItemChange(index, 'unitPrice', e.target.value)}
                              placeholder="0.00"
                              disabled={lineItem.isFromPO} // Read-only for items from PO
                            />
                          </Form.Group>
                        </Col>
                      )}
                      <Col md={isProductionUnitReceive || internalStoGrn ? 3 : 2}>
                        <Form.Group>
                          <Form.Label className="text-nowrap">Line Total</Form.Label>
                          <Form.Control
                            type="text"
                            value={lineItem.lineTotal || '0.00'}
                            readOnly
                            className="bg-body-secondary"
                            title={lineItem.serviceCostAdjustment > 0 ? `Base: ${(parseFloat(lineItem.lineTotal || 0) - parseFloat(lineItem.serviceCostAdjustment || 0)).toFixed(2)}, Service Cost: ${parseFloat(lineItem.serviceCostAdjustment || 0).toFixed(2)}` : ''}
                          />
                          {lineItem.serviceCostAdjustment > 0 && (
                            <Form.Text className="text-muted small">
                              +{parseFloat(lineItem.serviceCostAdjustment).toFixed(2)} service
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="g-2 mt-1">
                      {!isProductionUnitReceive && !internalStoGrn && (
                        <>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="text-nowrap">Additional Costs</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                value={lineItem.additionalCosts || ''}
                                onChange={(e) => handleGRNLineItemChange(index, 'additionalCosts', e.target.value)}
                                placeholder="0.00"
                                title="Freight, insurance, customs, handling, etc."
                              />
                              <Form.Text className="text-muted small">
                                Freight, insurance, customs, etc.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="text-nowrap">Allocated Service Cost</Form.Label>
                              <Form.Control
                                type="text"
                                value={parseFloat(lineItem.allocatedServiceCost || 0).toFixed(2)}
                                readOnly
                                className="bg-body-secondary"
                                title="Service costs allocated to this lot"
                              />
                            </Form.Group>
                          </Col>
                        </>
                      )}
                      <Col md={isProductionUnitReceive || internalStoGrn ? 6 : 3}>
                        <Form.Group>
                          <Form.Label className="text-nowrap">Landed Cost</Form.Label>
                          <Form.Control
                            type="text"
                            value={parseFloat(lineItem.landedCost || 0).toFixed(2)}
                            readOnly
                            className="bg-body-secondary fw-bold"
                            title={
                              isProductionUnitReceive || internalStoGrn
                                ? 'Carried from warehouse issue cost (GIN / lot landed cost)'
                                : 'Total landed cost = Base cost + Service cost + Additional costs'
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={isProductionUnitReceive || internalStoGrn ? 6 : 3}>
                        <Form.Group>
                          <Form.Label className="text-nowrap">Landed Cost/Unit</Form.Label>
                          <Form.Control
                            type="text"
                            value={parseFloat(lineItem.landedCostPerUnit || 0).toFixed(4)}
                            readOnly
                            className="bg-body-secondary"
                            title={
                              isProductionUnitReceive || internalStoGrn
                                ? 'Carried from warehouse issue cost (GIN / lot landed cost)'
                                : 'Landed cost per unit = Landed cost / Received quantity'
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    {(() => {
                      const refUpper = getGrnFormReferenceTypeUpper();
                      // Hide weigh bridge and vendor lot for work order and internal stock transfer GRNs
                      if (refUpper === 'WORK_ORDER' || refUpper === 'STOCK_TRANSFER_ORDER') {
                        return null;
                      }
                      
                      return (
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Weigh Bridge Receipt</Form.Label>
                              <Form.Select
                                value={lineItem.weighBridgeReceiptId || ''}
                                onChange={(e) => handleGRNLineItemChange(index, 'weighBridgeReceiptId', e.target.value)}
                              >
                                <option value="">Select Weigh Bridge Receipt (optional)</option>
                                {(() => {
                                  const selectedIdsOnOtherLines = new Set(
                                    grnLineItems
                                      .map((li, i) => (i !== index && li.weighBridgeReceiptId ? String(li.weighBridgeReceiptId) : null))
                                      .filter(Boolean)
                                  );
                                  const availableWBRs = unusedWeighBridgeReceipts.filter(wbr => {
                                    if (selectedIdsOnOtherLines.has(String(wbr.id))) {
                                      return false;
                                    }
                                    if (lineItem.itemId && wbr.material?.id
                                      && String(wbr.material.id) !== String(lineItem.itemId)) {
                                      return false;
                                    }
                                    if (grnFormData.procurementSurveyId) {
                                      // Prefer WBRs linked to this survey; allow unlinked WBRs for the same material
                                      if (wbr.procurementSurvey?.id
                                        && String(wbr.procurementSurvey.id) !== String(grnFormData.procurementSurveyId)) {
                                        return false;
                                      }
                                      if (wbr.purchaseOrder?.id && !wbr.procurementSurvey?.id) {
                                        return false;
                                      }
                                    }
                                    if (grnFormData.purchaseOrderId) {
                                      if (wbr.purchaseOrder?.id
                                        && String(wbr.purchaseOrder.id) !== String(grnFormData.purchaseOrderId)) {
                                        return false;
                                      }
                                      if (wbr.procurementSurvey?.id && !wbr.purchaseOrder?.id) {
                                        return false;
                                      }
                                    }
                                    return true;
                                  });

                                  return availableWBRs.map(wbr => {
                                    const net = wbr.netWeight != null
                                      ? parseFloat(wbr.netWeight).toFixed(2)
                                      : '';
                                    const materialName = wbr.material?.name || '';
                                    const vehicle = wbr.vehicleNo ? ` / ${wbr.vehicleNo}` : '';
                                    const ref = wbr.procurementSurvey?.surveyNumber
                                      ? ` / PS ${wbr.procurementSurvey.surveyNumber}`
                                      : (wbr.purchaseOrder?.poNumber ? ` / PO ${wbr.purchaseOrder.poNumber}` : '');
                                    return (
                                      <option key={wbr.id} value={wbr.id}>
                                        {wbr.rstNumber}
                                        {net ? ` — ${net}` : ''}
                                        {materialName ? ` ${materialName}` : ''}
                                        {vehicle}
                                        {ref}
                                      </option>
                                    );
                                  });
                                })()}
                              </Form.Select>
                              <Form.Text className="text-muted">
                                Only unused weigh bridge receipts (not yet on a GRN). Selecting one fills received quantity from net weight.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Vendor Lot Number</Form.Label>
                          <Form.Control
                            type="text"
                            value={lineItem.vendorLotNumber || ''}
                            onChange={(e) => handleGRNLineItemChange(index, 'vendorLotNumber', e.target.value)}
                            placeholder="Enter vendor lot number"
                          />
                        </Form.Group>
                      </Col>
                      {expiryEnabled && (
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Expiry date (optional)</Form.Label>
                            <DateInput
                              value={lineItem.expiryDate || ''}
                              onChange={(e) => handleGRNLineItemChange(index, 'expiryDate', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      )}
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Internal Lot Number</Form.Label>
                          <Form.Control
                            type="text"
                            value={lineItem.internalLotNumber || ''}
                            onChange={(e) => handleGRNLineItemChange(index, 'internalLotNumber', e.target.value)}
                            placeholder="Auto-generated"
                            readOnly
                            className="bg-body-secondary"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                      );
                    })()}
                  </Card.Body>
                </Card>
              );
              })}
            </div>

            {/* Service Items — PO-driven or manual (Procurement Survey / ad-hoc) */}
            {(() => {
              const refUpper = getGrnFormReferenceTypeUpper();
              if (refUpper === 'WORK_ORDER' || refUpper === 'STOCK_TRANSFER_ORDER' || refUpper === 'SALES_ORDER') {
                return null;
              }
              const allowManualServices = refUpper === 'PROCUREMENT_SURVEY'
                || (refUpper === 'PURCHASE_ORDER' && !grnFormData.purchaseOrderId);
              if (!allowManualServices && grnServiceItems.length === 0) {
                return null;
              }

              const addManualServiceRow = () => {
                setGrnServiceItems([
                  ...grnServiceItems,
                  {
                    serviceId: '',
                    serviceName: '',
                    vendorId: '',
                    vendor: null,
                    quantity: '',
                    unitPrice: '',
                    lineTotal: 0,
                    linkedMaterialLineItemIndex: grnLineItems.findIndex(li => li.itemId) >= 0
                      ? String(grnLineItems.findIndex(li => li.itemId))
                      : '',
                    linkedMaterialItemId: null,
                    fromPO: false
                  }
                ]);
              };

              return (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0 fw-bold">Service Items</Form.Label>
                  {allowManualServices && (
                    <Button size="sm" variant="outline-secondary" type="button" onClick={addManualServiceRow}>
                      + Add Service
                    </Button>
                  )}
                </div>
                {grnServiceItems.length === 0 ? (
                  <Form.Text className="text-muted">
                    Optional — add H&amp;T or other services (costs post to AP and landed cost when GRN is Received/Completed).
                  </Form.Text>
                ) : (
                <Card className="bg-body-secondary">
                  <Card.Body>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Quantity</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                          <th>Linked To Material Item</th>
                          <th>Vendor</th>
                          {allowManualServices && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {grnServiceItems.map((serviceItem, serviceItemIndex) => {
                          let linkedMaterialIndex = null;
                          let linkedMaterialItem = null;
                          
                          if (serviceItem.linkedMaterialLineItemIndex !== '' && 
                              serviceItem.linkedMaterialLineItemIndex !== null && 
                              serviceItem.linkedMaterialLineItemIndex !== undefined) {
                            const index = parseInt(serviceItem.linkedMaterialLineItemIndex);
                            if (!isNaN(index) && index >= 0 && index < grnLineItems.length) {
                              linkedMaterialIndex = index;
                              linkedMaterialItem = grnLineItems[index];
                            }
                          } else if (serviceItem.linkedMaterialItemId) {
                            const serviceItemId = String(serviceItem.linkedMaterialItemId);
                            const foundIndex = grnLineItems.findIndex(li => {
                              if (!li.itemId) return false;
                              return String(li.itemId) === serviceItemId;
                            });
                            
                            if (foundIndex >= 0 && foundIndex < grnLineItems.length) {
                              linkedMaterialIndex = foundIndex;
                              linkedMaterialItem = grnLineItems[foundIndex];
                            }
                          }
                          
                          const isLinked = linkedMaterialItem !== null || 
                            (serviceItem.linkedMaterialItemId !== null && serviceItem.linkedMaterialItemId !== undefined) ||
                            (serviceItem.linkedMaterialLineItemIndex !== '' && serviceItem.linkedMaterialLineItemIndex !== null && serviceItem.linkedMaterialLineItemIndex !== undefined);
                          
                          let displayQuantity;
                          if (isLinked && linkedMaterialItem) {
                            displayQuantity = parseFloat(linkedMaterialItem.receivedQuantity || 0);
                          } else if (isLinked && serviceItem.linkedMaterialItemId) {
                            const serviceItemId = String(serviceItem.linkedMaterialItemId);
                            const materialItem = grnLineItems.find(li => li.itemId && String(li.itemId) === serviceItemId);
                            if (materialItem) {
                              displayQuantity = parseFloat(materialItem.receivedQuantity || 0);
                            } else {
                              displayQuantity = parseFloat(serviceItem.quantity || 0);
                            }
                          } else {
                            displayQuantity = parseFloat(serviceItem.quantity || 0);
                          }
                          
                          const displayLineTotal = displayQuantity * (parseFloat(serviceItem.unitPrice) || 0);
                          const editable = !serviceItem.fromPO;
                          const catalogServices = organizationServices
                            .map(os => os.service)
                            .filter(Boolean);
                          
                          return (
                            <tr key={serviceItemIndex}>
                              <td>
                                {editable ? (
                                  <Form.Select
                                    size="sm"
                                    value={serviceItem.serviceId || ''}
                                    onChange={(e) => {
                                      const updated = [...grnServiceItems];
                                      const svc = catalogServices.find(s => String(s.id) === e.target.value);
                                      updated[serviceItemIndex].serviceId = e.target.value;
                                      updated[serviceItemIndex].serviceName = svc?.name || '';
                                      if (svc?.hourlyRate != null && !updated[serviceItemIndex].unitPrice) {
                                        updated[serviceItemIndex].unitPrice = svc.hourlyRate;
                                      } else if (svc?.fixedPrice != null && !updated[serviceItemIndex].unitPrice) {
                                        updated[serviceItemIndex].unitPrice = svc.fixedPrice;
                                      }
                                      setGrnServiceItems(updated);
                                    }}
                                    required={!!serviceItem.vendorId || !!serviceItem.unitPrice}
                                  >
                                    <option value="">Select service</option>
                                    {catalogServices.map(s => (
                                      <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                  </Form.Select>
                                ) : (
                                  serviceItem.serviceName
                                )}
                              </td>
                              <td>
                                {isLinked ? (
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    value={displayQuantity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    readOnly
                                    className="bg-body-secondary"
                                    title="Quantity matches linked material item (read-only)"
                                  />
                                ) : (
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    step="0.01"
                                    min="0"
                                    value={displayQuantity}
                                    onChange={(e) => {
                                      const newQuantity = parseFloat(e.target.value) || 0;
                                      const updated = [...grnServiceItems];
                                      updated[serviceItemIndex].quantity = newQuantity;
                                      updated[serviceItemIndex].lineTotal = newQuantity * (parseFloat(updated[serviceItemIndex].unitPrice) || 0);
                                      setGrnServiceItems(updated);
                                    }}
                                  />
                                )}
                              </td>
                              <td>
                                {editable ? (
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    step="0.01"
                                    min="0"
                                    value={serviceItem.unitPrice ?? ''}
                                    onChange={(e) => {
                                      const updated = [...grnServiceItems];
                                      updated[serviceItemIndex].unitPrice = e.target.value;
                                      const qty = parseFloat(displayQuantity) || 0;
                                      updated[serviceItemIndex].lineTotal = qty * (parseFloat(e.target.value) || 0);
                                      setGrnServiceItems(updated);
                                    }}
                                  />
                                ) : (
                                  formatCurrency(serviceItem.unitPrice)
                                )}
                              </td>
                              <td>{formatCurrency(displayLineTotal)}</td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={linkedMaterialIndex !== null && linkedMaterialIndex !== undefined ? linkedMaterialIndex.toString() : ''}
                                  onChange={(e) => {
                                    const updated = [...grnServiceItems];
                                    const selectedIndex = e.target.value;
                                    updated[serviceItemIndex].linkedMaterialLineItemIndex = selectedIndex;
                                    
                                    if (selectedIndex !== '' && selectedIndex !== null) {
                                      const materialIndex = parseInt(selectedIndex);
                                      if (materialIndex >= 0 && materialIndex < grnLineItems.length) {
                                        const materialQty = parseFloat(grnLineItems[materialIndex].receivedQuantity || 0);
                                        updated[serviceItemIndex].quantity = materialQty;
                                        updated[serviceItemIndex].lineTotal = materialQty * (parseFloat(updated[serviceItemIndex].unitPrice) || 0);
                                      }
                                    } else {
                                      updated[serviceItemIndex].linkedMaterialLineItemIndex = '';
                                    }
                                    setGrnServiceItems(updated);
                                  }}
                                >
                                  <option value="">None (Distributed to all items)</option>
                                  {grnLineItems
                                    .map((li, idx) => ({ li, idx }))
                                    .filter(({ li }) => li.itemId && li.itemId !== '')
                                    .map(({ li, idx }) => {
                                      const item = items.find(i => i.id === parseInt(li.itemId));
                                      return (
                                        <option key={idx} value={idx}>
                                          {item?.name || `Item ${li.itemId}`}
                                        </option>
                                      );
                                    })}
                                </Form.Select>
                              </td>
                              <td>
                                {editable ? (
                                  <Form.Select
                                    size="sm"
                                    value={serviceItem.vendorId || serviceItem.vendor?.id || ''}
                                    onChange={(e) => {
                                      const updated = [...grnServiceItems];
                                      updated[serviceItemIndex].vendorId = e.target.value;
                                      updated[serviceItemIndex].vendor = vendors.find(v => String(v.id) === e.target.value) || null;
                                      setGrnServiceItems(updated);
                                    }}
                                  >
                                    <option value="">Select vendor</option>
                                    {vendors.filter(v => v.isActive !== false).map(v => (
                                      <option key={v.id} value={v.id}>{v.name}</option>
                                    ))}
                                  </Form.Select>
                                ) : (
                                  serviceItem.vendor?.name || '-'
                                )}
                              </td>
                              {allowManualServices && (
                                <td>
                                  {editable && (
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      type="button"
                                      onClick={() => setGrnServiceItems(grnServiceItems.filter((_, i) => i !== serviceItemIndex))}
                                    >
                                      ×
                                    </Button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
                )}
              </div>
              );
            })()}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGRNModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedGRN ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* GIN Modal */}
      <Modal show={showGINModal} onHide={() => setShowGINModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{selectedGIN ? 'Edit GIN' : 'Create GIN'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveGIN}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>GIN Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={ginFormData.ginNumber}
                    onChange={(e) => setGinFormData({ ...ginFormData, ginNumber: e.target.value })}
                    placeholder="Auto-generated if left empty"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>GIN Date *</Form.Label>
                  <DateInput
                   
                    value={ginFormData.ginDate}
                    onChange={(e) => setGinFormData({ ...ginFormData, ginDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Movement Type *</Form.Label>
                  <Form.Select
                    value={ginFormData.movementTypeId}
                    onChange={(e) => handleGINMovementTypeChange(e.target.value)}
                    required
                  >
                    <option value="">Select Movement Type</option>
                    {outboundMovementTypes.filter(mt => mt.isActive).map(mt => (
                      <option key={mt.id} value={mt.id}>{mt.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Outbound movement types (e.g., Issue to Customer, Return to Vendor, Issue to Production)</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Issuing Location *</Form.Label>
                  <LocationSelect
                    warehouses={warehouses}
                    retailStores={retailStores}
                    productionUnits={productionUnits}
                    restrictToType={(() => {
                      const selectedMt = outboundMovementTypes.find(mt => {
                        const mtId = typeof mt.id === 'string' ? parseInt(mt.id) : mt.id;
                        const formId = typeof ginFormData.movementTypeId === 'string' ? parseInt(ginFormData.movementTypeId) : ginFormData.movementTypeId;
                        return mtId === formId;
                      });
                      return selectedMt?.locationType || null;
                    })()}
                    value={
                      ginFormData.issuingLocationType && ginFormData.issuingLocationId
                        ? `${ginFormData.issuingLocationType}:${ginFormData.issuingLocationId}`
                        : ''
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      const [locType, id] = value ? value.split(':') : ['WAREHOUSE', ''];
                      const normalizedType = (locType || 'WAREHOUSE').toUpperCase();
                      setGinFormData({
                        ...ginFormData,
                        warehouseId: normalizedType === 'WAREHOUSE' ? id : '',
                        issuingLocationType: normalizedType,
                        issuingLocationId: id,
                        workOrderId: '',
                        purchaseOrderId: '',
                        stockTransferOrderId: ''
                      });
                      setGinLineItems([{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '', grnLineItemId: '', unitPrice: '' }]);
                    }}
                  />
                  <Form.Text className="text-muted">Where materials are being issued from</Form.Text>
                </Form.Group>
              </Col>
              {/* Reference Order - dynamically changes based on Movement Type */}
              {(() => {
                // Find the selected movement type - handle both string and number ID comparisons
                const selectedMovementType = outboundMovementTypes.find(mt => {
                  const mtId = typeof mt.id === 'string' ? parseInt(mt.id) : mt.id;
                  const formId = typeof ginFormData.movementTypeId === 'string' ? parseInt(ginFormData.movementTypeId) : ginFormData.movementTypeId;
                  return mtId === formId;
                });
                
                // Get referenceType from the movement type, default to SALES_ORDER
                let referenceType = 'SALES_ORDER';
                if (selectedMovementType) {
                  referenceType = selectedMovementType.referenceType || 'SALES_ORDER';
                  // Normalize to uppercase for consistency
                  referenceType = referenceType.toUpperCase();
                  
                  // Fallback: If referenceType is not set, try to infer from movement type name
                  if (!selectedMovementType.referenceType || selectedMovementType.referenceType.trim() === '') {
                    const name = (selectedMovementType.name || '').toUpperCase();
                    if (name.includes('PRODUCTION') || name.includes('WORK ORDER') || name.includes('WORKORDER')) {
                      referenceType = 'WORK_ORDER';
                    } else if (name.includes('VENDOR') || name.includes('RETURN') || name.includes('PURCHASE')) {
                      referenceType = 'PURCHASE_ORDER';
                    } else if (name.includes('TRANSFER') || name.includes('STOCK TRANSFER')) {
                      referenceType = 'STOCK_TRANSFER_ORDER';
                    } else if (name.includes('SALE') || name.includes('CUSTOMER')) {
                      referenceType = 'SALES_ORDER';
                    }
                  }
                }
                
                // Get the label for the reference order based on referenceType
                const getReferenceOrderLabel = (refType) => {
                  switch (refType) {
                    case 'WORK_ORDER':
                      return 'Work Order';
                    case 'PURCHASE_ORDER':
                      return 'Purchase Order';
                    case 'STOCK_TRANSFER_ORDER':
                      return 'Stock Transfer Order';
                    case 'SALES_BILL':
                      return 'Sale Bill';
                    case 'SALES_ORDER':
                    default:
                      return 'Sales Order';
                  }
                };

                const referenceOrderLabel = getReferenceOrderLabel(referenceType);
                
                // Detect Issue to Production Unit movement types (by movement type name)
                const isIssueToProductionUnit =
                  selectedMovementType &&
                  (selectedMovementType.name || '').toUpperCase().includes('PRODUCTION UNIT');
                
                if (referenceType === 'WORK_ORDER') {
                  return (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Order ({referenceOrderLabel}) *</Form.Label>
                        <Form.Select
                          value={ginFormData.workOrderId}
                          onChange={(e) => handleGINWorkOrderChange(e.target.value)}
                          disabled={!ginFormData.issuingLocationId}
                          required
                        >
                          <option value="">Select {referenceOrderLabel}</option>
                          {workOrders
                            // For Issue to Production Unit, only show APPROVED and IN_PROGRESS work orders
                            .filter(wo => !isIssueToProductionUnit || wo.status === 'APPROVED' || wo.status === 'IN_PROGRESS')
                            .map(wo => (
                              <option key={wo.id} value={wo.id}>
                                {wo.workOrderNumber} {wo.workOrderDate ? `(${formatDate(wo.workOrderDate)})` : ''} - {wo.bom?.bomNumber || ''}
                              </option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {!ginFormData.issuingLocationId
                            ? 'Please select an issuing location first'
                            : 'Materials may be issued from any location against this work order'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  );
                } else if (referenceType === 'PURCHASE_ORDER') {
                  const isWarehouseIssuing = (ginFormData.issuingLocationType || 'WAREHOUSE') === 'WAREHOUSE';
                  return (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Order ({referenceOrderLabel})</Form.Label>
                        <Form.Select
                          value={ginFormData.purchaseOrderId}
                          onChange={(e) => setGinFormData({ ...ginFormData, purchaseOrderId: e.target.value, workOrderId: '', stockTransferOrderId: '' })}
                          disabled={!ginFormData.issuingLocationId || !isWarehouseIssuing}
                        >
                          <option value="">Select {referenceOrderLabel}</option>
                          {isWarehouseIssuing && purchaseOrders
                            .filter(po => !ginFormData.issuingLocationId || po.warehouse?.id === parseInt(ginFormData.issuingLocationId))
                            .map(po => (
                              <option key={po.id} value={po.id}>
                                {po.poNumber} {po.poDate ? `(${formatDate(po.poDate)})` : ''}
                              </option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {!ginFormData.issuingLocationId
                            ? 'Please select an issuing location first'
                            : !isWarehouseIssuing
                              ? 'Purchase Order returns require a Warehouse issuing location'
                              : `${referenceOrderLabel}s filtered by selected warehouse`}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  );
                } else if (referenceType === 'STOCK_TRANSFER_ORDER') {
                  return (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Order ({referenceOrderLabel}) *</Form.Label>
                        <Form.Select
                          value={ginFormData.stockTransferOrderId}
                          onChange={(e) => handleGINStockTransferOrderChange(e.target.value)}
                          disabled={!ginFormData.issuingLocationId}
                          required
                        >
                          <option value="">Select {referenceOrderLabel}</option>
                          {stockTransferOrders
                            .filter(sto => {
                              // A GIN issues against an STO that's been authorized (Approved) or already
                              // has a prior installment issued (In Transit). Keep the currently-selected
                              // STO visible on edit even if it has since moved on (e.g. to COMPLETED).
                              const isCurrentSelection = ginFormData.stockTransferOrderId
                                && String(sto.id) === String(ginFormData.stockTransferOrderId);
                              if (sto.status !== 'APPROVED' && sto.status !== 'IN_TRANSIT' && !isCurrentSelection) return false;
                              if (!ginFormData.issuingLocationId) return true;
                              const type = ginFormData.issuingLocationType || 'WAREHOUSE';
                              if (type === 'WAREHOUSE' && sto.sourceLocationType === 'WAREHOUSE' && sto.sourceWarehouse?.id) {
                                return sto.sourceWarehouse.id === parseInt(ginFormData.issuingLocationId);
                              }
                              if (type === 'RETAIL_STORE' && sto.sourceLocationType === 'RETAIL_STORE' && sto.sourceRetailStore?.id) {
                                return sto.sourceRetailStore.id === parseInt(ginFormData.issuingLocationId);
                              }
                              if (type === 'PRODUCTION_UNIT' && sto.sourceLocationType === 'PRODUCTION_UNIT' && sto.sourceProductionUnit?.id) {
                                return sto.sourceProductionUnit.id === parseInt(ginFormData.issuingLocationId);
                              }
                              return false;
                            })
                            .map(sto => (
                              <option key={sto.id} value={sto.id}>
                                {sto.transferNumber} {sto.transferDate ? `(${formatDate(sto.transferDate)})` : ''}
                              </option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {!ginFormData.issuingLocationId ? 'Please select an issuing location first' : `Approved or In Transit ${referenceOrderLabel}s filtered by source location`}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  );
                } else if (referenceType === 'SALES_BILL') {
                  // GINs for Sale Bills are only ever auto-created by the Sale Bill itself (Sales tab)
                  // when it's issued, to avoid double-counting against the bill - no manual picker here.
                  return (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Order ({referenceOrderLabel})</Form.Label>
                        <Form.Control type="text" value={ginFormData.referenceNumber ? `Sale Bill: ${ginFormData.referenceNumber}` : ''} disabled />
                        <Form.Text className="text-muted">
                          GINs for Sale Bills are created automatically when a bill is issued from Sales → Sale Bill.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  );
                } else {
                  // SALES_ORDER - placeholder for future implementation
                  return (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Order ({referenceOrderLabel})</Form.Label>
                        <Form.Control
                          type="text"
                          value={ginFormData.referenceNumber}
                          onChange={(e) => setGinFormData({ ...ginFormData, referenceNumber: e.target.value })}
                          placeholder="Enter Sales Order number (manual entry)"
                        />
                        <Form.Text className="text-muted">
                          Sales Order dropdown will be available in a future update. Use Reference Number field for now.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  );
                }
              })()}
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={ginFormData.status}
                    onChange={(e) => setGinFormData({ ...ginFormData, status: e.target.value })}
                  >
                    <option value="DRAFT">DRAFT</option>
                    <option value="ISSUED">ISSUED</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name</Form.Label>
                  {(() => {
                    const selectedMovementType = outboundMovementTypes.find(mt => {
                      const mtId = typeof mt.id === 'string' ? parseInt(mt.id) : mt.id;
                      const formId = typeof ginFormData.movementTypeId === 'string' ? parseInt(ginFormData.movementTypeId) : ginFormData.movementTypeId;
                      return mtId === formId;
                    });
                    const isIssueToProductionUnit = selectedMovementType && (selectedMovementType.name || '').toLowerCase().includes('production unit');
                    if (isIssueToProductionUnit) {
                      return (
                        <Form.Select
                          value={ginFormData.customerName}
                          onChange={(e) => setGinFormData({ ...ginFormData, customerName: e.target.value })}
                        >
                          <option value="">Select Production Unit</option>
                          {productionUnits.filter(pu => pu.isActive !== false).map(pu => (
                            <option key={pu.id} value={pu.name}>{pu.name}</option>
                          ))}
                        </Form.Select>
                      );
                    }
                    return (
                      <Form.Control
                        type="text"
                        value={ginFormData.customerName}
                        onChange={(e) => setGinFormData({ ...ginFormData, customerName: e.target.value })}
                      />
                    );
                  })()}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Department</Form.Label>
                  {(() => {
                    const selectedMovementType = outboundMovementTypes.find(mt => {
                      const mtId = typeof mt.id === 'string' ? parseInt(mt.id) : mt.id;
                      const formId = typeof ginFormData.movementTypeId === 'string' ? parseInt(ginFormData.movementTypeId) : ginFormData.movementTypeId;
                      return mtId === formId;
                    });
                    const isIssueToProductionUnit = selectedMovementType && (selectedMovementType.name || '').toLowerCase().includes('production unit');
                    if (isIssueToProductionUnit) {
                      return (
                        <Form.Select
                          value={ginFormData.department}
                          onChange={(e) => setGinFormData({ ...ginFormData, department: e.target.value })}
                        >
                          <option value="Production">Production</option>
                        </Form.Select>
                      );
                    }
                    return (
                      <Form.Control
                        type="text"
                        value={ginFormData.department}
                        onChange={(e) => setGinFormData({ ...ginFormData, department: e.target.value })}
                      />
                    );
                  })()}
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={ginFormData.remarks}
                onChange={(e) => setGinFormData({ ...ginFormData, remarks: e.target.value })}
              />
            </Form.Group>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Line Items *</Form.Label>
                <Button size="sm" variant="outline-primary" onClick={handleAddGINLineItem}>
                  + Add Item
                </Button>
              </div>
              <Form.Text className="d-block text-muted mb-2">
                You can issue both raw materials (from Org Admin → Materials) and finished goods (Products). Select the item from the dropdown; products are labelled [Product].
                <br />
                <strong>Requested Qty</strong> is the planned quantity (WO BOM pending or STO qty when linked).
                <strong> Issue Qty</strong> is what is actually taken from stock.
                Availability at the issuing location is shown on the Inventory Lot (Avail).
              </Form.Text>
              {ginLineItems.map((lineItem, index) => (
                <Card key={index} className="mb-2">
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Item *</Form.Label>
                          <Form.Select
                            value={lineItem.itemId}
                            onChange={(e) => handleGINLineItemChange(index, 'itemId', e.target.value)}
                            required
                          >
                            <option value="">Select Item (Material or Product)</option>
                            {(() => {
                              // Combined list: materials (raw) + products (finished goods) by item id, deduped
                              const seenIds = new Set();
                              const materialOpts = (organizationMaterials || [])
                                .filter(om => om.item && (om.item.isActive !== false))
                                .filter((om, idx, arr) => arr.findIndex(m => m.item?.id === om.item?.id) === idx)
                                .map(om => {
                                  seenIds.add(om.item.id);
                                  return { id: om.item.id, label: `${om.item.name}${om.sku ? ` (${om.sku})` : ''}`, type: 'Material' };
                                });
                              const productOpts = (products || [])
                                .filter(p => p.item && p.item.id && (p.isActive !== false))
                                .filter(p => !seenIds.has(p.item.id))
                                .map(p => {
                                  seenIds.add(p.item.id);
                                  return { id: p.item.id, label: `${p.item.name}${p.sku ? ` (${p.sku})` : ''} [Product]`, type: 'Product' };
                                });
                              return [...materialOpts, ...productOpts].map(opt => (
                                <option key={opt.id} value={opt.id}>{opt.label}</option>
                              ));
                            })()}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      {ginFormData.issuingLocationId && lineItem.itemId && (
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>Inventory Lot *</Form.Label>
                            <Form.Select
                              value={lineItem.grnLineItemId || ''}
                              onChange={(e) => handleGINLineItemLotSelect(index, e.target.value)}
                              required
                            >
                              <option value="">Select lot</option>
                              {inventoryLotsForGIN
                                .filter(lot => {
                                  const matchItem = lot.itemId != null && String(lot.itemId) === String(lineItem.itemId);
                                  const available = Number(lot.availableQuantity);
                                  const hasAvail = available > 0;
                                  const isCurrentSelection = lineItem.grnLineItemId && lot.id === parseInt(lineItem.grnLineItemId, 10);
                                  return matchItem && (hasAvail || isCurrentSelection);
                                })
                                // Where expiry dates are kept, the lot that goes bad first is offered first (lots without a date last)
                                .sort((a, b) => (expiryEnabled ? String(a.expiryDate || '9999').localeCompare(String(b.expiryDate || '9999')) : 0))
                                .map(lot => (
                                  <option key={lot.id} value={lot.id}>
                                    {lot.internalLotNumber || `Lot ${lot.id}`}{lot.vendorLotNumber ? ` / ${lot.vendorLotNumber}` : ''}{expiryEnabled && lot.expiryDate ? ` / ${lot.expiryDate < todayISO() ? 'EXPIRED' : 'expires'} ${formatDate(lot.expiryDate)}` : ''} - {lot.itemName || ''} (Avail: {Number(lot.availableQuantity).toFixed(2)}{lot.uomSymbol ? ` ${lot.uomSymbol}` : ''})
                                  </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">Required. Choose the lot to issue from; unit price = its landed cost.</Form.Text>
                            {expiryEnabled && (() => {
                              const picked = inventoryLotsForGIN.find((l) => String(l.id) === String(lineItem.grnLineItemId));
                              return picked?.expiryDate && picked.expiryDate < todayISO() ? (
                                <Form.Text className="d-block text-danger">This lot expired on {formatDate(picked.expiryDate)}. Issue it only if that is intended.</Form.Text>
                              ) : null;
                            })()}
                            {lineItem.grnLineItemId && getAvailableQtyForLotLine(index) !== null && (
                              <Form.Text className="d-block text-info">
                                Available in selected lot: {getAvailableQtyForLotLine(index).toFixed(3)}
                              </Form.Text>
                            )}
                            {!lineItem.grnLineItemId && getWarehouseAvailableQtyForLine(index) !== null && (
                              <Form.Text className="d-block text-info">
                                Available in {
                                  ginFormData.issuingLocationType === 'RETAIL_STORE' ? 'retail store'
                                    : ginFormData.issuingLocationType === 'PRODUCTION_UNIT' ? 'production unit'
                                    : 'warehouse'
                                }: {getWarehouseAvailableQtyForLine(index).toFixed(3)}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      )}
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Requested Qty *</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.001"
                            value={lineItem.quantity}
                            onChange={(e) => handleGINLineItemChange(index, 'quantity', e.target.value)}
                            required
                          />
                          <Form.Text className="text-muted">
                            Planned qty to issue (from WO BOM / STO when linked). Not the issuing location's stock.
                          </Form.Text>
                          {ginFormData.workOrderId && lineItem.woRequiredQty != null && (
                            <Form.Text className="d-block text-info">
                              WO Required: {(parseFloat(lineItem.woRequiredQty) || 0).toFixed(3)}
                              {' · '}Already Issued: {(parseFloat(lineItem.woAlreadyIssued) || 0).toFixed(3)}
                              {' · '}Remaining: {(parseFloat(lineItem.woRemainingQty) || 0).toFixed(3)}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>UOM</Form.Label>
                          <Form.Select
                            value={lineItem.unitOfMeasurementId}
                            onChange={(e) => handleGINLineItemChange(index, 'unitOfMeasurementId', e.target.value)}
                          >
                            <option value="">Select UOM</option>
                            {uoms.map(uom => (
                              <option key={uom.id} value={uom.id}>{uom.name} ({uom.symbol})</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Issue Qty</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.001"
                            value={lineItem.issuedQuantity}
                            onChange={(e) => handleGINLineItemChange(index, 'issuedQuantity', e.target.value)}
                            placeholder="Defaults to Requested Qty"
                          />
                          <Form.Text className="text-muted">
                            Actual qty taken from stock. Leave blank to use Requested Qty.
                          </Form.Text>
                          {getLotOvershootError(index) && (
                            <Form.Text className="d-block text-danger">
                              {getLotOvershootError(index)}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Unit Price</Form.Label>
                          <Form.Control
                            type="text"
                            readOnly
                            value={lineItem.unitPrice !== '' && !isNaN(parseFloat(lineItem.unitPrice)) ? parseFloat(lineItem.unitPrice).toFixed(2) : ''}
                            placeholder="From lot (landed cost)"
                          />
                          {lineItem.unitPrice !== '' && (
                            <Form.Text className="text-muted">Landed cost from selected lot</Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={7}>
                        <Form.Group>
                          <Form.Label>Remarks</Form.Label>
                          <Form.Control
                            type="text"
                            value={lineItem.remarks}
                            onChange={(e) => handleGINLineItemChange(index, 'remarks', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2} className="d-flex align-items-end justify-content-end">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleRemoveGINLineItem(index)}
                          disabled={ginLineItems.length === 1}
                          title="Remove line"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                            <path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                          </svg>
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGINModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={ginLineItems.some((_, idx) => !!getLotOvershootError(idx))}
              title={ginLineItems.some((_, idx) => !!getLotOvershootError(idx)) ? 'Fix lot quantity overshoot before saving' : ''}
            >
              {selectedGIN ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Print View Modal */}
      <Modal 
        show={showPrintModal} 
        onHide={() => setShowPrintModal(false)} 
        size="xl"
        className="print-modal"
      >
        <Modal.Header className="print-header">
          <Modal.Title>
            {printType === 'GRN' && 'Goods Receipt Note'}
            {printType === 'GIN' && 'Goods Issue Note'}
            {printType === 'STO' && 'Stock Transfer Order'}
          </Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">
              Print
            </Button>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>
              Close
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
          {printType === 'GRN' && printData && (() => {
            const isStockTransferGrnPrint = !!printData.stockTransferOrder;
            return (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>GOODS RECEIPT NOTE</h2>
                <p className="text-muted">GRN Number: <strong>{printData.grnNumber}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>GRN Date:</strong> {formatDate(printData.grnDate)}</p>
                  <p>
                    <strong>Receiving Location:</strong>{' '}
                    {printData.warehouse?.name || printData.retailStore?.name || printData.productionUnit?.name || '-'}
                  </p>
                  {(printData.purchaseOrder || printData.workOrder || printData.stockTransferOrder) && (
                    <p>
                      <strong>Reference Order:</strong>{' '}
                      {printData.purchaseOrder?.poNumber
                        ? `PO: ${printData.purchaseOrder.poNumber}`
                        : printData.workOrder?.workOrderNumber
                          ? `WO: ${printData.workOrder.workOrderNumber}`
                          : printData.stockTransferOrder?.transferNumber
                            ? `STO: ${printData.stockTransferOrder.transferNumber}`
                            : '-'}
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  <p><strong>Status:</strong> {printData.status}</p>
                </Col>
              </Row>
              {printData.lineItems && printData.lineItems.length > 0 && (
                <>
                  <h5 className="mt-3 mb-2">Goods Received</h5>
                  <Table striped bordered className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        {!isStockTransferGrnPrint && <th>Vendor</th>}
                        <th>Quantity</th>
                        {!isStockTransferGrnPrint && <th>Unit Price</th>}
                        <th>Line Total</th>
                        {!isStockTransferGrnPrint && <th>Weigh Bridge Receipt</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {printData.lineItems.map((item, idx) => {
                        const uom = item.unitOfMeasurement?.symbol || item.unitOfMeasurement?.name || '';
                        const quantity = parseFloat(item.receivedQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        return (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{item.item?.name || '-'}</td>
                            {!isStockTransferGrnPrint && <td>{item.vendor?.name || '-'}</td>}
                            <td>{quantity}{uom ? ` ${uom}` : ''}</td>
                            {!isStockTransferGrnPrint && <td>{formatCurrency(item.unitPrice)}</td>}
                            <td>{formatCurrency(item.lineTotal)}</td>
                            {!isStockTransferGrnPrint && <td>{item.weighBridgeReceiptNo || '-'}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}
              
              {printData.serviceItems && printData.serviceItems.length > 0 && (
                <>
                  <h5 className="mt-4 mb-2">Services Received</h5>
                  <Table striped bordered className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Service</th>
                        <th>Vendor</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Line Total</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printData.serviceItems.map((serviceItem, idx) => {
                        const uom = serviceItem.unitOfMeasurement?.symbol || serviceItem.unitOfMeasurement?.name || '';
                        const quantity = parseFloat(serviceItem.quantity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        return (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{serviceItem.service?.name || '-'}</td>
                            <td>{serviceItem.vendor?.name || '-'}</td>
                            <td>{quantity}{uom ? ` ${uom}` : ''}</td>
                            <td>{formatCurrency(serviceItem.unitPrice)}</td>
                            <td>{formatCurrency(serviceItem.lineTotal)}</td>
                            <td>{serviceItem.remarks || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}
              
              {(printData.lineItems?.length > 0 || printData.serviceItems?.length > 0) && (
                <div className="mt-3">
                  <Table bordered>
                    <tfoot>
                      <tr>
                        <td colSpan={isStockTransferGrnPrint ? 3 : 5} className="text-end"><strong>Total Amount:</strong></td>
                        <td colSpan={isStockTransferGrnPrint ? 1 : 2}>
                          <strong>
                            {formatCurrency(
                              (printData.lineItems || []).reduce((sum, item) => 
                                sum + (parseFloat(item.lineTotal || 0)), 0
                              ) +
                              (printData.serviceItems || []).reduce((sum, serviceItem) => 
                                sum + (parseFloat(serviceItem.lineTotal || 0)), 0
                              )
                            )}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}
              {printData.remarks && (
                <div className="mt-3">
                  <p><strong>Remarks:</strong> {printData.remarks}</p>
                </div>
              )}
            </div>
            );
          })()}
          {printType === 'GIN' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>GOODS ISSUE NOTE</h2>
                <p className="text-muted">GIN Number: <strong>{printData.ginNumber}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>GIN Date:</strong> {formatDate(printData.ginDate)}</p>
                  <p><strong>Issuing Location:</strong> {formatGinIssueLocation(printData)}</p>
                  {printData.goodsMovementType && (
                    <p><strong>Movement Type:</strong> {printData.goodsMovementType.name || '-'}</p>
                  )}
                  {printData.workOrder && (
                    <p><strong>Work Order:</strong> {printData.workOrder.workOrderNumber || '-'}</p>
                  )}
                  {printData.purchaseOrder && (
                    <p><strong>Purchase Order:</strong> {printData.purchaseOrder.poNumber || '-'}</p>
                  )}
                  {printData.stockTransferOrder && (
                    <p><strong>Stock Transfer Order:</strong> {printData.stockTransferOrder.transferNumber || '-'}</p>
                  )}
                  {printData.saleBill && (
                    <p><strong>Sale Bill:</strong> {printData.saleBill.billNumber || '-'}</p>
                  )}
                </Col>
                <Col md={6}>
                  <p><strong>Status:</strong> {printData.status}</p>
                  {(printData.customerName || printData.department) && (
                    <p><strong>Customer/Department:</strong> {printData.customerName || printData.department || '-'}</p>
                  )}
                  {printData.referenceNumber && (
                    <p><strong>Reference Number:</strong> {printData.referenceNumber}</p>
                  )}
                </Col>
              </Row>
              {printData.lineItems && printData.lineItems.length > 0 && (
                <>
                  <h5 className="mt-3 mb-2">Items Issued</h5>
                  <Table striped bordered className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Requested Qty</th>
                        <th>Issue Qty</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printData.lineItems.map((item, idx) => {
                        const uom = item.unitOfMeasurement?.symbol || item.unitOfMeasurement?.name || '';
                        const requestedStr = item.quantity != null ? parseFloat(item.quantity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
                        const issuedStr = item.issuedQuantity != null ? parseFloat(item.issuedQuantity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
                        return (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{item.item?.name || '-'}</td>
                          <td>{requestedStr}{uom ? ` ${uom}` : ''}</td>
                          <td>{issuedStr}{uom ? ` ${uom}` : ''}</td>
                          <td>{item.remarks || '-'}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}
              {printData.remarks && (
                <div className="mt-3">
                  <p><strong>Remarks:</strong> {printData.remarks}</p>
                </div>
              )}
            </div>
          )}
          {printType === 'STO' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>STOCK TRANSFER ORDER</h2>
                <p className="text-muted">
                  Transfer: <strong>{printData.transferNumber || '-'}</strong>
                </p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Transfer Date:</strong> {formatDate(printData.transferDate) || '-'}</p>
                  <p><strong>Source:</strong> {getStockTransferOrderLocationDisplay(printData, 'source')}</p>
                  <p><strong>Destination:</strong> {getStockTransferOrderLocationDisplay(printData, 'destination')}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Status:</strong> {printData.status || '-'}</p>
                </Col>
              </Row>
              {printData.remarks && (
                <div className="mb-3">
                  <p className="mb-0"><strong>Remarks:</strong> {printData.remarks}</p>
                </div>
              )}
              {printData.lineItems && printData.lineItems.length > 0 && (
                <>
                  <h5 className="mt-3 mb-2">Line Items</h5>
                  <Table striped bordered className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Quantity</th>
                        <th>UOM</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printData.lineItems.map((li, idx) => {
                        const uom = li.unitOfMeasurement?.symbol || li.unitOfMeasurement?.name || '';
                        const qtyStr =
                          li.quantity != null
                            ? parseFloat(li.quantity).toLocaleString('en-US', {
                                minimumFractionDigits: 3,
                                maximumFractionDigits: 3
                              })
                            : '-';
                        return (
                          <tr key={li.id || idx}>
                            <td>{idx + 1}</td>
                            <td>{li.item?.name || '-'}</td>
                            <td>{qtyStr}</td>
                            <td>{uom || '-'}</td>
                            <td>{li.remarks || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}

              <div className="mt-4">
                <h5 className="mb-2">Goods Issue Notes (GINs)</h5>
                <p className="text-muted small mb-2">Issued against this stock transfer order</p>
                {printData.stoTransactions?.gins?.length > 0 ? (
                  <Table striped bordered size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>GIN Number</th>
                        <th>Date</th>
                        <th>Location</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printData.stoTransactions.gins.map((g) => (
                        <tr key={`print-gin-${g.id}`}>
                          <td>{g.ginNumber || '-'}</td>
                          <td>{formatDate(g.ginDate) || '-'}</td>
                          <td>{g.locationName || '-'}</td>
                          <td>{g.status || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted mb-4">No GINs issued against this Stock Transfer Order.</p>
                )}
              </div>

              <div className="mt-4">
                <h5 className="mb-2">Goods Receipt Notes (GRNs)</h5>
                <p className="text-muted small mb-2">Received against this stock transfer order</p>
                {printData.stoTransactions?.grns?.length > 0 ? (
                  <Table striped bordered size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>GRN Number</th>
                        <th>Date</th>
                        <th>Location</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printData.stoTransactions.grns.map((g) => (
                        <tr key={`print-grn-${g.id}`}>
                          <td>{g.grnNumber || '-'}</td>
                          <td>{formatDate(g.grnDate) || '-'}</td>
                          <td>{g.locationName || '-'}</td>
                          <td>{g.status || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted">No GRNs received against this Stock Transfer Order.</p>
                )}
              </div>
            </div>
          )}
                  </OrgPrintChrome>
        </Modal.Body>
      </Modal>

      {/* Help Pane */}

      <GstRegistrationModal
        show={Boolean(ewayTransfer)}
        docType="STOCK_TRANSFER"
        docId={ewayTransfer?.id}
        label={ewayTransfer?.transferNumber}
        apiBase={ewayTransfer ? `/api/eway-movements/stock-transfers/${ewayTransfer.id}` : undefined}
        onHide={() => setEwayTransfer(null)}
        canCancel={hasAnyRole(user, ['ORG_ADMIN', 'INV_MGR', 'PROD_MGR'])}
      />
    </Container>
  );
}

export default Inventory;
